import * as st from './st';
import { applySpellCharges } from './spellCharges';
import { buildWeaponCardDescription, HEAL_EFFECT_IDS, LIFESTEAL_EFFECT_IDS } from './weapon';
import { applyWeaponMagingPreview } from './weaponMaging';
import { resolveMediaUrl } from '../utils/mediaUrl';

const TXT_EN = { fr: 'fr', en: 'en', de: 'de', es: 'es', pt: 'pt' };
const TXT_WITH_INFECTED = { fr: 'Avec [Infecté]', en: 'With [Infected]', de: 'Mit [Infiziert]', es: 'Con [Infectado]', pt: 'Com [Infectado]' };

const ELS = ['fire', 'air', 'water', 'earth', 'neutral'];
const HEAL_LBL = {
    fr: 'Soin',
    en: 'Heal',
    de: 'Heilung',
    es: 'Cura',
    pt: 'Cura',
};
const CLIENT_ONLY_LBL = {
    fr: 'Conditionnel',
    en: 'Conditional',
    de: 'Bedingt',
    es: 'Condicional',
    pt: 'Condicional',
};
const EL_NAMES = {
    earth:   { fr: 'Terre',  en: 'Earth', de: 'Erde', es: 'Tierra', pt: 'Terra'   },
    fire:    { fr: 'Feu',    en: 'Fire', de: 'Feuer', es: 'Fuego', pt: 'Fogo'    },
    water:   { fr: 'Eau',    en: 'Water', de: 'Wasser', es: 'Agua', pt: 'Água'   },
    air:     { fr: 'Air',    en: 'Air', de: 'Luft', es: 'Aire', pt: 'Ar'     },
    neutral: { fr: 'Neutre', en: 'Neutral', de: 'Neutral', es: 'Neutral', pt: 'Neutro' },
};
const COND_LBL = {
    target_under_element_state: {
        fr: 'Cible sous {element}',
        en: 'Target under {element}',
        de: 'Ziel unter {element}',
        es: 'Objetivo bajo {element}',
        pt: 'Alvo sob {element}',
    },
    percent_caster_hp: {
        fr: '% PV du lanceur',
        en: '% caster HP',
        de: '% LP des Zauberers',
        es: '% PdV del lanzador',
        pt: '% PV do lançador',
    },
    percent_caster_missing_hp: {
        fr: '% PV manquants',
        en: '% caster missing HP',
        de: '% fehlende LP',
        es: '% PdV que faltan',
        pt: '% PV faltantes',
    },
    health_steal: {
        fr: 'Vol de vie',
        en: 'Health steal',
        de: 'Lebensdiebstahl',
        es: 'Robo de vida',
        pt: 'Roubo de vida',
    },
    target_under_state: {
        fr: 'Cible sous état',
        en: 'Target under state',
        de: 'Ziel unter Zustand',
        es: 'Objetivo bajo estado',
        pt: 'Alvo sob estado',
    },
    caster_best_element: {
        fr: 'Meilleur élément',
        en: "Best element",
        de: "Bestes Element",
        es: "Mejor elemento",
        pt: "Melhor elemento",
    },
};

const CLS_TO_ID = {
    feca: 1,
    osamodas: 2,
    enutrof: 3,
    sram: 4,
    xelor: 5,
    ecaflip: 6,
    eniripsa: 7,
    iop: 8,
    cra: 9,
    sadida: 10,
    sacrieur: 11,
    pandawa: 12,
    roublard: 13,
    zobal: 14,
    steamer: 15,
    eliotrope: 16,
    huppermage: 17,
    ouginak: 18,
    forgelance: 20,
};

const ELM_STAT_ID = {
    fire: st.INTELLIGENCE_ID,
    air: st.AGILITY_ID,
    water: st.CHANCE_ID,
    earth: st.STRENGTH_ID,
    neutral: st.STRENGTH_ID,
};

const ELM_DMG_ID = {
    fire: 89,
    air: 91,
    water: 90,
    earth: 88,
    neutral: 92,
};
const HEAL_STAT_ID = 49; // soins fixes
const WEAPON_PER_DMG_ID = 122;
const FINAL_PER_DMG_ID = st.FINAL_DAMAGE_ID;
const PERCENT_MAX_HP_HEAL_EFFECT_IDS = new Set([1109]);
const BEST_ELEMENT_HEAL_EFFECT_IDS = new Set([3002]);

const toNum = (v) => (Number.isFinite(v) ? v : (Number.isFinite(Number(v)) ? Number(v) : 0));

const getCur = (stats, id) => toNum(stats?.[id]?.current);
const isPercentMaxHpHealEffectId = (id) => id != null && PERCENT_MAX_HP_HEAL_EFFECT_IDS.has(toNum(id));
const isBestElementHealEffectId = (id) => id != null && BEST_ELEMENT_HEAL_EFFECT_IDS.has(toNum(id));
const getBuildRangeBonus = (stats) => getCur(stats, st.RANGE_ID);

const applyBuildRangeBonus = (rangeMax, canBeBoosted, stats) => {
    if (canBeBoosted !== true || rangeMax == null) return rangeMax;
    return toNum(rangeMax) + getBuildRangeBonus(stats);
};

export const isPercentMaxHpHealLine = (line) =>
    line?.kind === 'heal' && isPercentMaxHpHealEffectId(line?.effectId);

export const getTypeId = (clsName) => CLS_TO_ID[clsName] ?? null;

/** Eliotrope spell cards only show through-portal effect lines (tooltip unchanged). */
export const isEliotropeClass = (clsName) => (clsName || '').toLowerCase() === 'eliotrope';

export const pickLvl = (lvls, lvl) => {
    if (!Array.isArray(lvls) || lvls.length === 0) return null;
    const sorted = [...lvls].sort((a, b) => toNum(a?.level) - toNum(b?.level));
    const byLvl = sorted.filter((x) => toNum(x?.level) <= lvl);
    if (byLvl.length > 0) return byLvl[byLvl.length - 1];
    return sorted[sorted.length - 1];
};

export const pickLvlAtOrBelow = (lvls, lvl) => {
    if (!Array.isArray(lvls) || lvls.length === 0) return null;
    const sorted = [...lvls].sort((a, b) => toNum(a?.level) - toNum(b?.level));
    const byLvl = sorted.filter((x) => toNum(x?.level) <= lvl);
    if (byLvl.length > 0) return byLvl[byLvl.length - 1];
    return null;
};

export const isSpellAvailableAtLevel = (spell, lvl) =>
    !!pickLvlAtOrBelow(spell?.levels, lvl);

const hasVal = (v) => v != null && Number.isFinite(Number(v));

const condId = (c) => c?.id ?? c?.Id;

const hasCond = (line, id) => {
    if (id === 'through_portal' && line?.through_portal === true) return true;
    return Array.isArray(line?.conditions) && line.conditions.some((c) => condId(c) === id);
};

/** DofusDB-style name object: fr, en, de, es, pt, … */
const pickI18n = (obj, lang) => {
    if (typeof obj === 'string' && obj.trim()) return obj;
    if (!obj || typeof obj !== 'object') return null;
    const tryOrder = [lang, 'en', 'fr', 'de', 'es', 'pt'].filter((k, i, a) => k && a.indexOf(k) === i);
    for (const k of tryOrder) {
        const v = obj[k];
        if (typeof v === 'string' && v.trim()) return v;
    }
    return null;
};

/** One entry per `target_under_state` in conditions (mask order). */
const collectTargetUnderStateSpecs = (line) => {
    if (!Array.isArray(line?.conditions)) return [];
    return line.conditions
        .filter((c) => condId(c) === 'target_under_state')
        .map((c) => {
            const stateId = c.state_id ?? c.stateId;
            const stateName = c.state_name ?? c.stateName ?? null;
            const nestedName = c.name && typeof c.name === 'object' ? c.name : null;
            const neg = c.state_negated ?? c.stateNegated;
            const tokenCase = c.state_token_case ?? c.stateTokenCase;
            return {
                state_id: stateId,
                state_name: stateName ?? nestedName,
                state_negated: neg === true,
                state_token_case: tokenCase === 'lower' || tokenCase === 'upper' ? tokenCase : 'upper',
            };
        });
};

/** Raw trigger; API normalizes missing to immediate (`I`). */
export const rawEffectTrigger = (line) => {
    const t = line?.triggers;
    if (t == null || t === '') return 'I';
    return String(t);
};

const lineDelay = (line) => toNum(line?.delay);

/** Not same-tick immediate (I + delay 0); timing from `triggers` / `delay` only. */
const isTimedEffect = (line) => {
    const r = rawEffectTrigger(line);
    if (r === 'TB' || r === 'TE') return true;
    return lineDelay(line) > 0;
};

const isEffectLineType = (line) =>
    line?.type === 'damage' || line?.type === 'heal' || line?.type === 'push';

/**
 * Mask-derived buckets only, then timing (triggers/delay).
 * conditions[]: through_portal | direct | target_under_state — never immediate/start_of_turn.
 */
const getLineCond = (line) => {
    if (hasCond(line, 'through_portal')) return { key: 'through_portal', stateId: null, stateSpecs: null };
    if (hasCond(line, 'direct')) return { key: 'direct', stateId: null, stateSpecs: null };
    const stateSpecs = collectTargetUnderStateSpecs(line);
    if (stateSpecs.length > 0) {
        const firstId = stateSpecs.find((s) => s.state_id != null)?.state_id ?? null;
        return { key: 'target_under_state', stateId: firstId, stateSpecs };
    }
    if (hasCond(line, 'target_under_state')) return { key: 'target_under_state', stateId: null, stateSpecs: [] };
    if (!isTimedEffect(line)) return null;
    if (rawEffectTrigger(line) === 'TE') return { key: 'end_of_turn', stateId: null, stateSpecs: null };
    return { key: 'start_of_turn', stateId: null, stateSpecs: null };
};

const condBucketKey = (cond, line) => {
    const tm = line?.target_mask ?? '';
    const timingSuffix = isTimedEffect(line)
        ? `:t:${lineDelay(line)}:${rawEffectTrigger(line)}`
        : '';
    if (cond.key === 'start_of_turn' || cond.key === 'end_of_turn') {
        return `${cond.key}:${tm}:${lineDelay(line)}:${rawEffectTrigger(line)}`;
    }
    if (cond.key === 'target_under_state' && Array.isArray(cond.stateSpecs) && cond.stateSpecs.length > 0) {
        const sig = cond.stateSpecs
            .map((s) => `${String(s.state_id ?? '')}:${s.state_negated ? 1 : 0}:${s.state_token_case || ''}`)
            .join(',');
        return `${cond.key}:${sig}:${tm}${timingSuffix}`;
    }
    if (cond.stateId != null) {
        return `${cond.key}:${cond.stateId}:${tm}${timingSuffix}`;
    }
    return `${cond.key}:${tm}${timingSuffix}`;
};

/** Immediate same-tick lines: trigger I, delay 0, no mask split / state gate / timed. */
const isBaseLine = (line) => {
    if (!isEffectLineType(line)) return false;
    if (isTimedEffect(line)) return false;
    if (hasCond(line, 'through_portal') || hasCond(line, 'direct')) return false;
    if (hasCond(line, 'target_under_state')) return false;
    return rawEffectTrigger(line) === 'I';
};

/** Labels for raw `triggers` (mirror backend EFFECT_TRIGGER_REGISTRY). */
export const EFFECT_TRIGGER_LABEL = {
    I: { fr: 'Immédiat', en: 'Immediate', de: 'Sofort', es: 'Inmediato', pt: 'Imediato' },
    TB: { fr: 'Début de tour', en: 'Start of turn', de: 'Zugbeginn', es: 'Inicio de turno', pt: 'Início de turno' },
    TE: { fr: 'Fin de tour', en: 'End of turn', de: 'Zugende', es: 'Fin de turno', pt: 'Fim de turno' },
};

export const fmtTriggerLabel = (raw, lang = 'fr') => {
    const l = TXT_EN[lang] || TXT_EN.en;
    const key = raw == null || raw === '' ? 'I' : String(raw);
    return EFFECT_TRIGGER_LABEL[key]?.[l] || key;
};

/**
 * Raw `effect_lines` filters for damage totals (see SPELLS_ENDPOINT.md).
 * Default: same-tick immediate only (`triggers` I, `delay` 0).
 */
export const filterDamageLinesThroughPortal = (effectLines, { sameTickOnly = true } = {}) =>
    (effectLines || []).filter((ln) => {
        if (ln?.type !== 'damage') return false;
        if (hasCond(ln, 'direct')) return false;
        if (sameTickOnly && (rawEffectTrigger(ln) !== 'I' || lineDelay(ln) !== 0)) return false;
        return true;
    });

export const filterDamageLinesWithoutPortal = (effectLines, { sameTickOnly = true } = {}) =>
    (effectLines || []).filter((ln) => {
        if (ln?.type !== 'damage') return false;
        if (hasCond(ln, 'through_portal')) return false;
        if (sameTickOnly && (rawEffectTrigger(ln) !== 'I' || lineDelay(ln) !== 0)) return false;
        return true;
    });

const COND_REGISTRY = {
    through_portal: { fr: 'Via portail', en: 'Through portal', de: 'Durch Portal', es: 'Por portal', pt: 'Via portal' },
    direct: { fr: 'Sans portail', en: 'Direct cast', de: 'Direkter Wirkung', es: 'Lanzamiento directo', pt: 'Lançamento direto' },
    target_under_state: { fr: 'Cible sous état', en: 'Target under state', de: 'Ziel unter Zustand', es: 'Objetivo bajo estado', pt: 'Alvo sob estado' },
    start_of_turn: { fr: 'Début de tour', en: 'Start of turn', de: 'Zugbeginn', es: 'Inicio de turno', pt: 'Início de turno' },
    end_of_turn: { fr: 'Fin de tour', en: 'End of turn', de: 'Zugende', es: 'Fin de turno', pt: 'Fim de turno' },
    allies: { fr: 'Alliés', en: 'Allies', de: 'Verbündete', es: 'Aliados', pt: 'Aliados' },
    health_steal: { fr: 'Vol de vie', en: 'Health steal', de: 'Lebensdiebstahl', es: 'Robo de vida', pt: 'Roubo de vida' },
    best_element_steal: { fr: 'Vol du meilleur élément', en: 'Best-element steal', de: 'Diebstahl des besten Elements', es: 'Robo del mejor elemento', pt: 'Roubo do melhor elemento' },
};

const ALLY_ENEMY_LBL = { fr: 'Allié / Ennemi', en: 'Ally / Enemy', de: 'Verbündeter / Gegner', es: 'Aliado / Enemigo', pt: 'Aliado / Inimigo' };

const SADIDA_INFECTION_STATE_IDS = new Set([263, 713, 5409]);
const SADIDA_INFECTION_STATE_NAMES = new Set(['[Infecté]', 'Infecté III', 'Infecté (effets sur cible)']);
const sadidaInfectionLabel = (lang) => (TXT_WITH_INFECTED[lang] || TXT_WITH_INFECTED.en);

const hasSadidaInfectionStateBundle = (stateSpecs) => {
    if (!Array.isArray(stateSpecs)) return false;
    const ids = new Set(
        stateSpecs
            .map((s) => Number(s?.state_id ?? s?.stateId))
            .filter((id) => Number.isFinite(id))
    );
    return [...SADIDA_INFECTION_STATE_IDS].every((id) => ids.has(id));
};

const hasSadidaInfectionNameBundle = (stateNames) => {
    if (!Array.isArray(stateNames)) return false;
    const names = new Set(stateNames.map((s) => pickI18n(s, 'fr') || String(s)).filter(Boolean));
    return [...SADIDA_INFECTION_STATE_NAMES].every((name) => names.has(name));
};

const SUR_INVOC_LBL = { fr: 'Sur invocation', en: 'On summons', de: 'Auf Beschwörungen', es: 'Sobre invocaciones', pt: 'Sobre invocações' };

const fallbackChargeCaption = (n, lang) => {
    const l = TXT_EN[lang] || TXT_EN.en;
    if (!Number.isFinite(n) || n < 1) return null;
    if (l === 'fr') return n === 1 ? '1 charge' : `${n} charges`;
    return n === 1 ? '1 charge' : `${n} charges`;
};

const stripMaximumPrefix = (s) => {
    if (typeof s !== 'string') return s;
    return s.replace(/^\s*(maximum|maximo|máximo)\s*:\s*/iu, '').trim();
};

const normalizeStateTemplateRefs = (s) => {
    if (typeof s !== 'string') return s;
    return s.replace(/\{\{spell,[^:}]+::([^}]+)\}\}/g, '[$1]').trim();
};

/**
 * FR/EN phrase for one spell-state gate.
 * API does not provide an explicit actor (target vs caster) for this condition,
 * so keep wording neutral to avoid incorrect subject labels.
 */
const fmtTargetUnderStateSpec = (spec, lang) => {
    const nmRaw = pickI18n(spec.state_name, lang) || (spec.state_id != null ? `#${spec.state_id}` : '');
    const nm = normalizeStateTemplateRefs(nmRaw);
    if (!nm) return null;
    const tok = spec?.state_token_case ?? spec?.stateTokenCase ?? '';
    const neg = tok === 'lower' ? true : (tok === 'upper' ? false : !!spec.state_negated);
    const l = TXT_EN[lang] || TXT_EN.en;
    if (l === 'fr') {
        return `${neg ? 'Sans' : 'Avec'} ${nm}`.replace(/\s+/g, ' ').trim();
    }
    return `${neg ? 'Without' : 'With'} ${nm}`.replace(/\s+/g, ' ').trim();
};

const stateSpecGroupKey = (spec, lang) => {
    const sid = spec?.state_id ?? spec?.stateId;
    if (sid != null && sid !== '') return `id:${sid}`;
    const nm = pickI18n(spec?.state_name, lang) || '';
    return `name:${nm.toLowerCase()}`;
};

const collapseStateSpecsForLabel = (stateSpecs, lang) => {
    const groups = new Map();
    (stateSpecs || []).forEach((spec) => {
        const key = stateSpecGroupKey(spec, lang);
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(spec);
    });
    const out = [];
    groups.forEach((items) => {
        const hasNeg = items.some((s) => !!s?.state_negated);
        const hasPos = items.some((s) => !s?.state_negated);
        const rep = items.find((s) => !s?.state_negated) || items[0];
        if (hasNeg && hasPos) {
            out.push({ ...rep, state_negated: true });
            return;
        }
        out.push(rep);
    });
    return out;
};

export const fmtCondLabel = (key, stateId, lang, withStartOfTurn = false, withEndOfTurn = false, stateSpecs = null) => {
    const l = TXT_EN[lang] || TXT_EN.en;
    const raw = COND_REGISTRY[key]?.[l] || key;
    let out;
    if (key === 'target_under_state' && Array.isArray(stateSpecs) && stateSpecs.length > 0) {
        const parts = hasSadidaInfectionStateBundle(stateSpecs)
            ? [sadidaInfectionLabel(lang)]
            : collapseStateSpecsForLabel(stateSpecs, lang)
                .map((s) => fmtTargetUnderStateSpec(s, lang))
                .filter(Boolean);
        out = parts.length > 0 ? parts.join(' · ') : (stateId != null ? `${raw} #${stateId}` : raw);
    } else {
        out = stateId != null ? `${raw} #${stateId}` : raw;
    }
    if (withStartOfTurn && key !== 'start_of_turn') {
        const sot = COND_REGISTRY.start_of_turn?.[l] || 'Start of turn';
        out = `${out} · ${sot}`;
    }
    if (withEndOfTurn && key !== 'end_of_turn') {
        const eot = COND_REGISTRY.end_of_turn?.[l] || 'End of turn';
        out = `${out} · ${eot}`;
    }
    return out;
};

const sumRanges = (acc, r) => ({
    min: (acc.min ?? 0) + toNum(r?.min),
    max: (acc.max ?? 0) + toNum(r?.max),
});

const sumCritRanges = (acc, r) => ({
    min: (acc.min ?? 0) + toNum(r?.crit_min),
    max: (acc.max ?? 0) + toNum(r?.crit_max),
});

const targetsAllies = (ln) => ln?.affects_allies === true && ln?.affects_enemies === false;

const normTargetMask = (ln) => {
    const tm = ln?.target_mask ?? ln?.targetMask;
    if (tm == null || tm === '') return '';
    return String(tm).replace(/\s/g, '');
};

/**
 * Single-letter mask tokens that are structural targeting / zones, not case-paired predicates.
 * (Do not strip these when building mask-XOR base keys.)
 */
const MASK_STRUCTURAL_SINGLE = new Set(['A', 'a', 'C', 'c', 'J', 'j', 'R', 'r', 'g', 'G']);

/** Spell-state tokens: E123 / e123 — not case-XOR predicates. */
const isMaskSpellStateToken = (t) => /^[Ee]\d+$/.test(t);

/**
 * Predicate tokens: same letter + optional digits with lower/upper case = mutually exclusive branches
 * (e.g. v50/V50, s/S). Excludes spell states and known single-char targeting letters.
 */
const isMaskCasePairPredicateToken = (t) => {
    const p = String(t || '').trim();
    if (!p) return false;
    if (isMaskSpellStateToken(p)) return false;
    if (MASK_STRUCTURAL_SINGLE.has(p)) return false;
    if (/^([a-zA-Z])(\d+)$/.test(p)) return true;
    if (/^[a-zA-Z]$/.test(p)) return true;
    return false;
};

/** Mask with all case-pair predicate tokens removed; null if nothing was stripped. */
export const maskXorBaseKey = (mask) => {
    const raw = mask == null ? '' : String(mask).replace(/\s/g, '');
    if (!raw) return null;
    const parts = raw.split(',').map((p) => p.trim()).filter(Boolean);
    const kept = parts.filter((p) => !isMaskCasePairPredicateToken(p));
    if (kept.length === parts.length) return null;
    return kept.join(',');
};

const firstMaskCasePairPredicateToken = (mask) => {
    const raw = mask == null ? '' : String(mask).replace(/\s/g, '');
    if (!raw) return null;
    return raw.split(',').map((p) => p.trim()).find(isMaskCasePairPredicateToken) || null;
};

const maskPredicateBranchLabel = (mask, lang = 'fr') => {
    const token = firstMaskCasePairPredicateToken(mask);
    if (!token) return null;
    const m = /^([a-zA-Z])(\d*)$/.exec(token);
    if (!m) return null;
    const l = TXT_EN[lang] || TXT_EN.en;
    const letter = m[1];
    const value = m[2];
    const upper = letter === letter.toUpperCase();
    const key = letter.toLowerCase();
    if (key === 'v' && value) {
        return upper
            ? (l === 'fr' ? `PV < ${value}%` : `HP < ${value}%`)
            : (l === 'fr' ? `PV ≥ ${value}%` : `HP ≥ ${value}%`);
    }
    return null;
};

/** Masks that only hit invocations (Dofus `J` / `j`), e.g. `J,j`. */
const isSummonOnlyTargetMask = (tm) => {
    const s = tm == null ? '' : String(tm).replace(/\s/g, '');
    if (!s) return false;
    return /^[Jj,]+$/.test(s);
};

const targetMaskKeySuffix = (ln) => {
    const s = normTargetMask(ln);
    if (!s) return '';
    return `|m:${s}`;
};

/** Caster MP remainder (DofusDB 1012–1016); peak row has localized `row_caption`. */
const MP_REMAINDER_EFFECT_IDS = new Set([1012, 1013, 1014, 1015, 1016]);

const isMpRemainderEffectId = (id) => id != null && MP_REMAINDER_EFFECT_IDS.has(toNum(id));

const effectIdKeySuffix = (ln) => {
    if (ln?.type !== 'damage') return '';
    const raw = ln?.effect_id ?? ln?.effectId;
    if (raw == null || raw === '') return '';
    const n = toNum(raw);
    if (!Number.isFinite(n) || n <= 0) return '';
    return `|e${n}`;
};

const randomKeySuffix = (ln) => {
    if (!ln) return '';
    const rawRnd = ln?.random ?? ln?.randomChance;
    const rawGrp = ln?.group ?? ln?.randomGroup;
    const rnd = Number(rawRnd);
    const grp = Number(rawGrp);
    const rk = Number.isFinite(rnd) && rnd > 0 ? `|r${rnd}` : '|r0';
    const gk = Number.isFinite(grp) ? `|g${grp}` : '|gx';
    return `${rk}${gk}`;
};

const clientOnlyKeySuffix = (ln) => {
    if (ln?.type !== 'damage') return '';
    const isClientOnly = ln?.for_client_only === true || ln?.forClientOnly === true;
    return isClientOnly ? '|co1' : '|co0';
};

const stackIdx = (ln) => {
    if (ln == null) return null;
    const v = ln.stack_index ?? ln.stackIndex;
    if (v == null || v === '') return null;
    const n = toNum(v);
    return Number.isFinite(n) ? n : null;
};

const parseDmgKey = (k) => {
    const parts = String(k).split('|');
    const el = parts[0];
    const isLifesteal = parts[1] === '1';
    let stackIndex = null;
    if (parts.length >= 3 && parts[2].startsWith('s')) {
        const n = Number(parts[2].slice(1));
        if (Number.isFinite(n)) stackIndex = n;
    }
    return { el, isLifesteal, stackIndex };
};

const parseHealKey = (k) => {
    const parts = String(k).split('|');
    return { el: parts[0] };
};

// Stable structural identity for a selectable effect line.
//
// A line is keyed by its *structure* — type, effect id, element, target, stack
// tier, embedding — never by its damage numbers. Crit dice and the localized
// row caption are deliberately excluded: they drift on every balance patch /
// locale change, and keying on them silently breaks frontend↔backend line
// matching (a non-matching line yields an empty solver criterion, so the spell
// is scored as a constant 0 and ignored).
//
// Base min/max are included ONLY for embedded rows (trap / glyph child-spell
// tiers), mirroring the dmgKey rangeK grouping where one carrier embeds several
// positional tiers that must stay distinct.
//
// Past the fixed structural fields we append the *display row* discriminators —
// every stable field the card uses to put two raw lines on different rows
// (trigger timing, delay, lifesteal, condition gate). Without them, two lines
// the card shows as separate rows (e.g. Feu de Brousse's immediate hit vs its
// `TE` end-of-turn hit — same effect/element/target) hash to one key, so
// selecting one row selects the other. These are appended only when non-default
// so plain immediate lines keep their existing key (and existing saved build
// criteria for them stay valid); the drifting fields (min/max/crit, caption)
// are still excluded. Voluntary grouping is unaffected — but NOT because grouped
// lines are identical (they need not be: e.g. Trait Ardent groups a push line
// and a fire-damage line into one row). A display row owns the *union* of its
// member lines' keys, and which lines land in a row is decided by the display
// keys (dmgKey / condBucketKey), never by this string. So refining this key only
// makes two *separate* rows get distinct key-sets; it can't merge or split a
// group. Selecting a grouped row still yields a multi-key criterion (the solver
// maximizes every line in it).
//
// MUST stay byte-for-byte in lockstep with backend effect_line_source_key
// (backend/gen/engine/spell_scoring.py).
const conditionGateSig = (ln) => {
    const conds = Array.isArray(ln?.conditions) ? ln.conditions : [];
    const toks = conds
        .map((c) => {
            const id = condId(c);
            if (id == null || id === '') return null;
            if (id === 'target_under_state') {
                const sid = c?.state_id ?? c?.stateId;
                const neg = (c?.state_negated ?? c?.stateNegated) === true ? 1 : 0;
                const tcRaw = c?.state_token_case ?? c?.stateTokenCase;
                const tc = (tcRaw === 'lower' || tcRaw === 'upper') ? tcRaw : 'upper';
                return `target_under_state:${sid == null ? '' : sid}:${neg}:${tc}`;
            }
            return String(id);
        })
        .filter(Boolean);
    toks.sort();
    return toks.join(',');
};

export const rawEffectLineKey = (ln) => {
    const type = ln?.type || '';
    const effectId = ln?.effect_id ?? ln?.effectId ?? '';
    const element = ln?.element || '';
    const targetMask = normTargetMask(ln) || '';
    const stackIndex = ln?.stack_index ?? ln?.stackIndex ?? '';
    const stackMax = ln?.stack_max ?? ln?.stackMax ?? '';
    const embeddedFrom = ln?.embedded_from_spell_id ?? ln?.embeddedFromSpellId ?? '';
    const carrier = ln?.embedding_carrier_effect_id ?? ln?.embeddingCarrierEffectId ?? '';
    const isEmbedded = embeddedFrom !== '' || carrier !== '';
    const parts = [
        type,
        effectId,
        element,
        targetMask,
        stackIndex,
        stackMax,
        embeddedFrom,
        carrier,
    ];
    if (isEmbedded) {
        parts.push(hasVal(ln?.min) ? toNum(ln.min) : '');
        parts.push(hasVal(ln?.max) ? toNum(ln.max) : '');
    }
    // Display-row discriminators (appended only when non-default; see note above).
    const trigger = rawEffectTrigger(ln);
    if (trigger && trigger !== 'I') parts.push(`tg${trigger}`);
    const delay = lineDelay(ln);
    if (Number.isFinite(delay) && delay > 0) parts.push(`dl${delay}`);
    if ((ln?.is_lifesteal ?? ln?.isLifesteal) === true) parts.push('ls');
    if ((ln?.for_client_only ?? ln?.forClientOnly) === true) parts.push('co');
    const gate = conditionGateSig(ln);
    if (gate) parts.push(`cd${gate}`);
    return parts.join('|');
};

const normalizeCriterionLineKeys = (criterion) => {
    if (!criterion || typeof criterion !== 'object') return [];
    const raw = criterion.line_keys ?? criterion.lineKeys ?? [];
    if (!Array.isArray(raw)) return [];
    return Array.from(new Set(
        raw.map((x) => String(x || '').trim()).filter(Boolean)
    )).sort();
};

export const isSpellCriterionAvailableAtLevel = ({
    criterion,
    spellById,
    lvl,
    rowById = null,
}) => {
    const spellId = Number(
        typeof criterion === 'number'
            ? criterion
            : (criterion?.spell_id ?? criterion?.spellId ?? criterion?.id)
    );
    if (!Number.isFinite(spellId)) return false;
    const spell = spellById instanceof Map ? spellById.get(spellId) : spellById?.[spellId];
    if (!spell) return false;
    const selectedLevel = pickLvlAtOrBelow(spell?.levels, lvl);
    if (!selectedLevel) return false;

    const lineKeys = normalizeCriterionLineKeys(criterion);
    if (!lineKeys.length) return true;

    const gatherRowSourceKeys = (row, out = new Set(), depth = 0) => {
        if (!row || depth > 6) return out;
        (row.baseLines || []).forEach((ln) => {
            (ln?.sourceKeys || []).forEach((k) => {
                const key = String(k || '').trim();
                if (key) out.add(key);
            });
        });
        (row.conditionalBlocks || []).forEach((block) => {
            (block?.lines || []).forEach((ln) => {
                (ln?.sourceKeys || []).forEach((k) => {
                    const key = String(k || '').trim();
                    if (key) out.add(key);
                });
            });
        });
        (row.summonChildRows || []).forEach((child) => gatherRowSourceKeys(child, out, depth + 1));
        return out;
    };

    if (rowById && typeof rowById.get === 'function') {
        const row = rowById.get(spellId);
        if (row) {
            const availableFromRows = gatherRowSourceKeys(row);
            if (availableFromRows.size > 0) {
                return lineKeys.every((k) => availableFromRows.has(k));
            }
        }
    }

    const available = new Set(
        (selectedLevel?.effect_lines || [])
            .filter((ln) => ln && typeof ln === 'object')
            .map(rawEffectLineKey)
            .map((x) => String(x || '').trim())
            .filter(Boolean)
    );
    if (!available.size) return false;
    return lineKeys.every((k) => available.has(k));
};

const isEmbeddedRawEffectLine = (ln) =>
    (ln?.embedded_from_spell_id ?? ln?.embeddedFromSpellId) != null
    || (ln?.embedding_carrier_effect_id ?? ln?.embeddingCarrierEffectId) != null;

const SUPPRESSED_EMBEDDED_FROM_SPELL_IDS = new Set([25507, 25508]);
const SUPPRESSED_EMBEDDED_CARRIER_EFFECT_IDS = new Set([2022]);
const isSuppressedEmbeddedRawEffectLine = (ln) => {
    if (!isEmbeddedRawEffectLine(ln)) return false;
    const sid = Number(ln?.embedded_from_spell_id ?? ln?.embeddedFromSpellId);
    if (Number.isFinite(sid) && SUPPRESSED_EMBEDDED_FROM_SPELL_IDS.has(sid)) return true;
    const carrierId = Number(ln?.embedding_carrier_effect_id ?? ln?.embeddingCarrierEffectId);
    return Number.isFinite(carrierId) && SUPPRESSED_EMBEDDED_CARRIER_EFFECT_IDS.has(carrierId);
};

const toLineArrays = (damageByEl, healByEl) => {
    const damageLines = Object.entries(damageByEl)
        .filter(([, v]) => hasVal(v.base?.min) || hasVal(v.base?.max))
        .map(([k, v]) => {
            const { el, isLifesteal, stackIndex: skFromKey } = parseDmgKey(k);
            return {
                kind: 'damage',
                el,
                base: v.base,
                crit: (hasVal(v.crit?.min) || hasVal(v.crit?.max)) ? v.crit : null,
                affectsAllies: v.affectsAllies ?? false,
                affectsEnemies: v.affectsEnemies ?? false,
                isLifesteal: !!isLifesteal,
                rowCaption: v.rowCaption,
                stackIndex: v.stackIndex ?? skFromKey,
                stackMax: v.stackMax,
                scalingKind: v.scalingKind ?? null,
                targetMask: v.targetMask ?? null,
                effectId: v.effectId ?? null,
                embeddingCarrierDescription: v.embeddingCarrierDescription ?? null,
                embeddingConditionStateNames: v.embeddingConditionStateNames ?? null,
                embeddedFromSpellId: v.embeddedFromSpellId ?? null,
                embeddingCarrierEffectId: v.embeddingCarrierEffectId ?? null,
                randomChance: v.randomChance ?? null,
                randomGroup: v.randomGroup ?? null,
                forClientOnly: v.forClientOnly === true,
                triggers: v.triggers ?? 'I',
                delay: v.delay ?? 0,
                elVariantXorKey: v.elVariantXorKey ?? null,
                sourceKeys: v.sourceKeys || [],
            };
        });
    const healLines = Object.entries(healByEl)
        .filter(([, v]) => hasVal(v.base?.min) || hasVal(v.base?.max))
        .map(([k, v]) => {
            const { el } = parseHealKey(k);
            return ({
                kind: 'heal',
                el,
                base: v.base,
                crit: (hasVal(v.crit?.min) || hasVal(v.crit?.max)) ? v.crit : null,
                affectsAllies: v.affectsAllies ?? false,
                targetMask: v.targetMask ?? null,
                effectId: v.effectId ?? null,
                randomChance: v.randomChance ?? null,
                randomGroup: v.randomGroup ?? null,
                elVariantXorKey: v.elVariantXorKey ?? null,
                sourceKeys: v.sourceKeys || [],
            });
        });
    return { damageLines, healLines };
};

const dmgKey = (el, isLifesteal, ln, uniqueLineKey = null) => {
    const si = ln?.type === 'damage' ? stackIdx(ln) : null;
    const mk = targetMaskKeySuffix(ln);
    const ek = effectIdKeySuffix(ln);
    const randK = randomKeySuffix(ln);
    const cok = clientOnlyKeySuffix(ln);
    const ceid = ln?.embedding_carrier_effect_id ?? ln?.embeddingCarrierEffectId;
    const ck = ceid != null ? `|c${ceid}` : '';
    const embeddedFrom = ln?.embedded_from_spell_id ?? ln?.embeddedFromSpellId;
    const isEmbedded = embeddedFrom != null || ceid != null;
    const rmin = hasVal(ln?.min) ? toNum(ln.min) : '';
    const rmax = hasVal(ln?.max) ? toNum(ln.max) : rmin;
    // Embedded (trap/glyph child spell) rows often represent alternative positional tiers, not additive hits.
    // Keep distinct raw ranges as separate lines instead of collapsing them into a summed bucket.
    const rangeK = isEmbedded ? `|rr${rmin}-${rmax}` : '';
    const uniq = uniqueLineKey ? `|u:${uniqueLineKey}` : '';
    if (si != null) return `${el}|${isLifesteal ? '1' : '0'}|s${si}${mk}${ek}${randK}${cok}${ck}${uniq}`;
    return `${el}|${isLifesteal ? '1' : '0'}${mk}${ek}${randK}${cok}${ck}${rangeK}${uniq}`;
};

const healKey = (el, ln, uniqueLineKey = null) => {
    const mk = targetMaskKeySuffix(ln);
    const randK = randomKeySuffix(ln);
    const uniq = uniqueLineKey ? `|u:${uniqueLineKey}` : '';
    return `${el}${mk}${randK}${uniq}`;
};

/** One push effect line → cell range (no merging across lines). */
const pushRangeFromLn = (ln) => {
    if (ln?.type !== 'push') return null;
    if (!hasVal(ln?.min) && !hasVal(ln?.max)) return null;
    let a = hasVal(ln.min) ? toNum(ln.min) : null;
    let b = hasVal(ln.max) ? toNum(ln.max) : null;
    if (a == null && b != null) a = b;
    if (b == null && a != null) b = a;
    if (a == null) return null;
    return {
        min: a,
        max: b,
        triggers: rawEffectTrigger(ln),
        delay: lineDelay(ln),
        sourceKeys: [rawEffectLineKey(ln)].filter(Boolean),
    };
};

const pushLineFromCells = (cells) => {
    if (!cells || (!hasVal(cells.min) && !hasVal(cells.max))) return null;
    return {
        kind: 'damage',
        el: 'push',
        base: cells,
        crit: null,
        triggers: cells.triggers ?? 'I',
        delay: cells.delay ?? 0,
        sourceKeys: cells.sourceKeys || [],
    };
};

const aggregateInto = (damageByEl, healByEl, ln, uniqueLineKey = null) => {
    const el = ln?.element || 'neutral';
    if (!ELS.includes(el)) return;
    const allies = targetsAllies(ln);
    const isLs = !!ln?.is_lifesteal;
    if (ln?.type === 'damage') {
        const key = dmgKey(el, isLs, ln, uniqueLineKey);
        // crit stays null until an effect line has crit_min/crit_max (avoid fake 0,0 → crit column).
        if (!damageByEl[key]) {
            damageByEl[key] = {
                base: { min: 0, max: 0 },
                crit: null,
                affectsAllies: false,
                affectsEnemies: false,
                rowCaption: ln.row_caption ?? ln.rowCaption,
                stackIndex: stackIdx(ln),
                stackMax: ln.stack_max ?? ln.stackMax,
                scalingKind: ln.scaling_kind ?? ln.scalingKind ?? null,
                targetMask: normTargetMask(ln) || null,
                effectId: (() => {
                    const raw = ln?.effect_id ?? ln?.effectId;
                    if (raw == null || raw === '') return null;
                    const n = toNum(raw);
                    return Number.isFinite(n) && n > 0 ? n : null;
                })(),
                embeddingCarrierDescription: ln.embedding_carrier_description ?? ln.embeddingCarrierDescription ?? null,
                embeddingConditionStateNames: ln.embedding_condition_state_names ?? ln.embeddingConditionStateNames ?? null,
                embeddedFromSpellId: (() => {
                    const raw = ln?.embedded_from_spell_id ?? ln?.embeddedFromSpellId;
                    if (raw == null || raw === '') return null;
                    const n = toNum(raw);
                    return Number.isFinite(n) && n > 0 ? n : null;
                })(),
                embeddingCarrierEffectId: (() => {
                    const raw = ln?.embedding_carrier_effect_id ?? ln?.embeddingCarrierEffectId;
                    if (raw == null || raw === '') return null;
                    const n = toNum(raw);
                    return Number.isFinite(n) && n > 0 ? n : null;
                })(),
                randomChance: (() => {
                    const raw = ln?.random;
                    const n = Number(raw);
                    return Number.isFinite(n) && n > 0 ? n : null;
                })(),
                randomGroup: (() => {
                    const raw = ln?.group;
                    const n = Number(raw);
                    return Number.isFinite(n) ? n : null;
                })(),
                forClientOnly: false,
                triggers: rawEffectTrigger(ln),
                delay: lineDelay(ln),
                elVariantXorKey: ln?.el_variant_xor_group ?? null,
                sourceKeys: [],
            };
        }
        const srcKey = rawEffectLineKey(ln);
        if (srcKey && !damageByEl[key].sourceKeys.includes(srcKey)) damageByEl[key].sourceKeys.push(srcKey);
        damageByEl[key].base = sumRanges(damageByEl[key].base, ln);
        if (hasVal(ln?.crit_min) || hasVal(ln?.crit_max)) {
            damageByEl[key].crit = sumCritRanges(damageByEl[key].crit || { min: 0, max: 0 }, ln);
        }
        damageByEl[key].affectsAllies = damageByEl[key].affectsAllies || allies;
        damageByEl[key].affectsEnemies = damageByEl[key].affectsEnemies || (ln?.affects_enemies === true);
        damageByEl[key].forClientOnly = damageByEl[key].forClientOnly
            || ln?.for_client_only === true
            || ln?.forClientOnly === true;
        if (damageByEl[key].triggers == null) damageByEl[key].triggers = rawEffectTrigger(ln);
        if (damageByEl[key].delay == null) damageByEl[key].delay = lineDelay(ln);
    } else if (ln?.type === 'heal') {
        const key = healKey(el, ln, uniqueLineKey);
        if (!healByEl[key]) {
            healByEl[key] = {
                base: { min: 0, max: 0 },
                crit: null,
                affectsAllies: false,
                targetMask: normTargetMask(ln) || null,
                effectId: (() => {
                    const raw = ln?.effect_id ?? ln?.effectId;
                    if (raw == null || raw === '') return null;
                    const n = toNum(raw);
                    return Number.isFinite(n) && n > 0 ? n : null;
                })(),
                randomChance: null,
                randomGroup: null,
                elVariantXorKey: ln?.el_variant_xor_group ?? null,
                sourceKeys: [],
            };
        }
        const srcKey = rawEffectLineKey(ln);
        if (srcKey && !healByEl[key].sourceKeys.includes(srcKey)) healByEl[key].sourceKeys.push(srcKey);
        healByEl[key].base = sumRanges(healByEl[key].base, { min: ln?.min, max: ln?.max });
        if (hasVal(ln?.crit_min) || hasVal(ln?.crit_max)) {
            healByEl[key].crit = sumCritRanges(healByEl[key].crit || { min: 0, max: 0 }, ln);
        }
        healByEl[key].affectsAllies = healByEl[key].affectsAllies || allies;
        const rnd = Number(ln?.random);
        if (Number.isFinite(rnd) && rnd > 0) healByEl[key].randomChance = rnd;
        const grp = Number(ln?.group);
        if (Number.isFinite(grp)) healByEl[key].randomGroup = grp;
    }
};

// Mirrors drop_duplicated_client_only_rows in backend/gen/engine/spell_scoring.py.
// Keep the two in step: a line the card totals but the solver drops (or vice versa)
// is a build that scores differently from what it displays.
// Deliberately strict: a spell often carries one row for the cast and another for a
// triggered/conditioned variant, and both must stay separately selectable. Only rows
// agreeing on slot, timing, gating AND numbers count as the same thing said twice.
const clientOnlyTwinSig = (ln) => JSON.stringify([
    ln?.type ?? null,
    ln?.effect_id ?? ln?.effectId ?? null,
    ln?.element ?? null,
    normTargetMask(ln) || '',
    rawEffectTrigger(ln),
    lineDelay(ln),
    ln?.min ?? null,
    ln?.max ?? null,
    ln?.crit_min ?? ln?.critMin ?? null,
    ln?.crit_max ?? ln?.critMax ?? null,
    stackIdx(ln),
    ln?.stack_max ?? ln?.stackMax ?? null,
    ln?.exclusive_branch_key ?? ln?.exclusiveBranchKey ?? null,
    ln?.conditions ?? null,
    ln?.is_lifesteal === true || ln?.isLifesteal === true,
    ln?.embedded_from_spell_id ?? ln?.embeddedFromSpellId ?? null,
]);

const isRawClientOnly = (ln) => ln?.for_client_only === true || ln?.forClientOnly === true;

const dropDuplicatedClientOnlyRows = (lines) => {
    if (!Array.isArray(lines) || !lines.length) return lines || [];
    const unflagged = new Set(
        lines.filter((ln) => ln && !isRawClientOnly(ln)).map(clientOnlyTwinSig),
    );
    if (!unflagged.size) return lines;
    return lines.filter((ln) => !(ln && isRawClientOnly(ln) && unflagged.has(clientOnlyTwinSig(ln))));
};

const buildLinesFromEffectLines = (lv) => {
    const lines = dropDuplicatedClientOnlyRows(lv?.effect_lines || []);
    const baseDmg = {};
    const baseHeal = {};
    const basePushLines = [];
    const condMap = {};
    const bonusRaw = [];
    for (let i = 0; i < lines.length; i += 1) {
        const ln = lines[i];
        const uniqueLineKey = `${i}:${rawEffectLineKey(ln) || ''}`;
        if (isSuppressedEmbeddedRawEffectLine(ln)) continue;
        if (ln?.type === 'base_damage_bonus') {
            bonusRaw.push(ln);
            continue;
        }
        const cond = getLineCond(ln);
        if (cond) {
            const k = condBucketKey(cond, ln);
            const raw = rawEffectTrigger(ln);
            if (!condMap[k]) {
                condMap[k] = {
                    key: cond.key,
                    stateId: cond.stateId,
                    stateSpecs: cond.stateSpecs,
                    damage: {},
                    heal: {},
                    pushLines: [],
                    hasStartOfTurn: false,
                    hasEndOfTurn: false,
                };
            }
            if (raw === 'TE') {
                condMap[k].hasEndOfTurn = true;
            } else if (raw === 'TB' || lineDelay(ln) > 0) {
                condMap[k].hasStartOfTurn = true;
            }
            if (ln.type === 'push') {
                const r = pushRangeFromLn(ln);
                if (r) condMap[k].pushLines.push(r);
            } else {
                aggregateInto(condMap[k].damage, condMap[k].heal, ln, uniqueLineKey);
            }
        } else if (isBaseLine(ln)) {
            if (ln.type === 'push') {
                const r = pushRangeFromLn(ln);
                if (r) basePushLines.push(r);
            } else {
                aggregateInto(baseDmg, baseHeal, ln, uniqueLineKey);
            }
        } else if (isEffectLineType(ln)) {
            if (ln.type === 'push') {
                const r = pushRangeFromLn(ln);
                if (r) basePushLines.push(r);
            } else {
                aggregateInto(baseDmg, baseHeal, ln, uniqueLineKey);
            }
        }
    }
    const QUAD_ELS = ['earth', 'fire', 'water', 'air'];
    const hasAllQuadEls = (set) => QUAD_ELS.every((el) => set.has(el));
    const numOrX = (v) => (Number.isFinite(Number(v)) ? Number(v) : 'x');
    const rangeSig = (r) => `${numOrX(r?.min)}:${numOrX(r?.max)}`;
    const baseDiceSig = (line) => `${rangeSig(line?.base)}|${rangeSig(line?.crit)}|${numOrX(line?.randomChance)}|${numOrX(line?.randomGroup)}`;
    const condSig = (line) => JSON.stringify(
        (line?.conditions || []).map((c) => ({
            id: c?.id ?? null,
            stateId: c?.state_id ?? c?.stateId ?? null,
            neg: c?.state_negated ?? c?.stateNegated ?? null,
            tok: c?.state_token_case ?? c?.stateTokenCase ?? null,
        })),
    );
    const chargeFamilyKey = (line) => {
        if (!line || line.kind !== 'damage') return null;
        if (!QUAD_ELS.includes(line.el)) return null;
        if (line.embeddedFromSpellId != null || line.embeddingCarrierEffectId != null) return null;
        return [
            line.targetMask || '',
            line.triggers ?? 'I',
            Number.isFinite(Number(line.delay)) ? Number(line.delay) : 0,
            line.affectsAllies ? 1 : 0,
            line.affectsEnemies ? 1 : 0,
            line.isLifesteal ? 1 : 0,
            condSig(line),
        ].join('|');
    };
    const normalizeQuadElementChargeProxy = (damageLines) => {
        if (!Array.isArray(damageLines) || damageLines.length < 2) return damageLines || [];
        const groups = new Map();
        damageLines.forEach((line, idx) => {
            const key = chargeFamilyKey(line);
            if (!key) return;
            if (!groups.has(key)) groups.set(key, []);
            groups.get(key).push({ line, idx });
        });
        if (!groups.size) return damageLines;
        let out = [...damageLines];
        groups.forEach((items) => {
            const baseCandidates = items
                .filter(({ line }) => line.stackIndex == null || line.stackIndex === 0)
                .map(({ line }) => line);
            const chargedCandidates = items
                .filter(({ line }) =>
                    Number.isFinite(Number(line.stackIndex))
                    && Number(line.stackIndex) >= 1
                    && Number.isFinite(Number(line.stackMax))
                    && Number(line.stackMax) >= 1)
                .map(({ line }) => line);
            if (!baseCandidates.length || !chargedCandidates.length) return;
            if (baseCandidates.length !== 4) return;
            if (chargedCandidates.length !== 1) return;
            const baseEls = new Set(baseCandidates.map((line) => line.el).filter(Boolean));
            const chargedEls = new Set(chargedCandidates.map((line) => line.el).filter(Boolean));
            if (!hasAllQuadEls(baseEls) || chargedEls.size !== 1) return;

            const representativeStack0Candidates = baseCandidates.filter((line) =>
                Number.isFinite(Number(line.stackIndex))
                && Number(line.stackIndex) === 0
                && Number.isFinite(Number(line.stackMax))
                && Number(line.stackMax) >= 1,
            );
            if (representativeStack0Candidates.length !== 1) return;
            const representativeStack0 = representativeStack0Candidates[0];
            const chargedLine = chargedCandidates[0];
            if (!baseCandidates.every((line) => baseDiceSig(line) === baseDiceSig(baseCandidates[0]))) return;
            if (Number(chargedLine.stackMax) !== Number(representativeStack0.stackMax)) return;
            if (chargedLine.effectId !== representativeStack0.effectId) return;
            if (chargedLine.el !== representativeStack0.el) return;

            if (representativeStack0) {
                out = out.map((line) => {
                    if (!line || line.kind !== 'damage' || !QUAD_ELS.includes(line.el)) return line;
                    if (line.stackIndex != null) return line;
                    if (!(line.stackMax == null || !Number.isFinite(Number(line.stackMax)))) return line;
                    if (chargeFamilyKey(line) !== chargeFamilyKey(representativeStack0)) return line;
                    return {
                        ...line,
                        stackIndex: 0,
                        stackMax: representativeStack0.stackMax,
                        rowCaption: line.rowCaption || representativeStack0.rowCaption,
                    };
                });
            }

            const representativeEl = chargedLine?.el;
            const missingEls = QUAD_ELS.filter((el) => !chargedEls.has(el));
            if (!representativeEl || !missingEls.length) return;
            const effectByEl = new Map();
            baseCandidates.forEach((line) => {
                if (line?.el) effectByEl.set(line.el, line.effectId ?? null);
            });
            const clones = [];
            chargedCandidates.forEach((line) => {
                if (line.el !== representativeEl) return;
                missingEls.forEach((el) => {
                    clones.push({
                        ...line,
                        el,
                        effectId: effectByEl.get(el) ?? line.effectId ?? null,
                    });
                });
            });
            if (clones.length) out = [...out, ...clones];
        });
        return out;
    };

    const damageLineSortTierRaw = (line) => {
        if (isSummonOnlyTargetMask(line.targetMask)) return 2;
        if (isMpRemainderEffectId(line.effectId)) return 1;
        return 0;
    };

    const sortDamageLinesByTarget = (damageLines) => {
        if (!damageLines?.length) return;
        damageLines.sort((a, b) => {
            const at = damageLineSortTierRaw(a);
            const bt = damageLineSortTierRaw(b);
            if (at !== bt) return at - bt;
            const ai = a.stackIndex;
            const bi = b.stackIndex;
            const au = ai == null || !Number.isFinite(ai);
            const bu = bi == null || !Number.isFinite(bi);
            if (au && bu) return 0;
            if (au) return -1;
            if (bu) return 1;
            return ai - bi;
        });
    };

    const baseArrays = toLineArrays(baseDmg, baseHeal);
    baseArrays.damageLines = normalizeQuadElementChargeProxy(baseArrays.damageLines);
    sortDamageLinesByTarget(baseArrays.damageLines);
    const base = { ...baseArrays, basePushLines };
    const conditional = Object.values(condMap)
        .filter((g) =>
            Object.keys(g.damage).length > 0
            || Object.keys(g.heal).length > 0
            || (g.pushLines && g.pushLines.length > 0))
        .map((g) => {
            const arr = toLineArrays(g.damage, g.heal);
            arr.damageLines = normalizeQuadElementChargeProxy(arr.damageLines);
            sortDamageLinesByTarget(arr.damageLines);
            return {
                key: g.key,
                stateId: g.stateId,
                stateSpecs: g.stateSpecs,
                hasStartOfTurn: g.hasStartOfTurn,
                hasEndOfTurn: g.hasEndOfTurn ?? false,
                pushLines: g.pushLines,
                ...arr,
            };
        });
    const bonusLines = bonusRaw.map((ln) => ({
        kind: 'base_damage_bonus',
        el: ELS.includes(ln?.element) ? ln.element : 'neutral',
        base: {
            min: hasVal(ln?.min) ? toNum(ln.min) : 0,
            max: hasVal(ln?.max) ? toNum(ln.max) : (hasVal(ln?.min) ? toNum(ln.min) : 0),
        },
        crit: (hasVal(ln?.crit_min) || hasVal(ln?.crit_max))
            ? {
                min: hasVal(ln?.crit_min) ? toNum(ln.crit_min) : null,
                max: hasVal(ln?.crit_max) ? toNum(ln.crit_max) : null,
            }
            : null,
        delay: ln?.delay,
        duration: ln?.duration ?? ln?.Duration,
        referenceSpellId: ln?.reference_spell_id ?? ln?.referenceSpellId,
        triggers: rawEffectTrigger(ln),
    }));
    return { base, conditional, bonusLines };
};

const fmtCond = (lv, lang, el, { dropTargetUnderStateLineHint = false } = {}) => {
    const key = lv?.damage_condition;
    if (!key) return null;
    if (dropTargetUnderStateLineHint && key === 'target_under_state') return null;
    const l = TXT_EN[lang] || TXT_EN.en;
    const raw = COND_LBL[key]?.[l] || key;
    const elLbl = l === 'fr'
        ? { fire: 'Feu', air: 'Air', water: 'Eau', earth: 'Terre', neutral: 'Neutre', push: 'Poussée' }
        : { fire: 'Fire', air: 'Air', water: 'Water', earth: 'Earth', neutral: 'Neutral', push: 'Push' };
    let text = raw.replace('{element}', elLbl[el] || el);
    const dmgStateId = lv?.damage_condition_state_id ?? lv?.damageConditionStateId;
    const dmgStateName = lv?.damage_condition_state_name ?? lv?.damageConditionStateName;
    if (key === 'target_under_state' && dmgStateId != null) {
        const nm = pickI18n(dmgStateName, lang);
        text = nm ? `${text} ${nm}` : `${text} #${dmgStateId}`;
    }
    return text;
};

export const getDmgLines = (lv) => {
    if (!lv) return { base: [], conditional: [], bonusLines: [] };
    const { base, conditional, bonusLines } = buildLinesFromEffectLines(lv);
    const basePushSynth = (base.basePushLines || []).map(pushLineFromCells).filter(Boolean);
    const baseOut = [...base.damageLines, ...basePushSynth, ...base.healLines];
    const condOut = conditional.map((g) => {
        const pushSynth = (g.pushLines || []).map(pushLineFromCells).filter(Boolean);
        const lines = [...g.damageLines, ...pushSynth, ...g.healLines];
        return {
            key: g.key,
            stateId: g.stateId,
            stateSpecs: g.stateSpecs,
            hasStartOfTurn: g.hasStartOfTurn,
            hasEndOfTurn: g.hasEndOfTurn ?? false,
            lines,
        };
    });
    return { base: baseOut, conditional: condOut, bonusLines: bonusLines || [] };
};

// Where the character fights, from the generator's None/Mêlée/Distance toggle
// (genConfig.position). Carried on the stats object under a symbol, so every
// damage formula below sees it without another parameter, and nothing that
// walks the characteristics (Object.entries, JSON) ever does.
const POSITION = Symbol('position');

/** The stats a card computes against, fighting at `position`. */
export const statsAtPosition = (stats, position) => (
    stats && position && position !== st.POSITION_NONE ? { ...stats, [POSITION]: position } : stats
);

// A declared side takes its own line; "none" declares no side, so neither
// counts. Mirror of score::Position (solver-core) and POSITION_DAMAGE_ID in
// gen/engine/spell_scoring.py.
const positionDamageMultiplier = (stats) => {
    const id = st.POSITION_DAMAGE_ID[stats?.[POSITION]];
    return id == null ? 1 : (100 + (getCur(stats, id) || 0)) / 100;
};

/** % Dommages sorts: final multiplier after base spell damage formula. */
const applySpellDmgFinalMult = (raw, stats) => {
    const spellPct = getCur(stats, st.SPELL_PER_DMG_ID) || 0;
    const finalPct = getCur(stats, FINAL_PER_DMG_ID) || 0;
    const mult = (1 + spellPct / 100) * (1 + finalPct / 100) * positionDamageMultiplier(stats);
    return Math.floor(toNum(raw) * mult);
};

const calcVal = ({ base, stats, el, isCrit }) => {
    const b = toNum(base);
    const pwr = getCur(stats, st.POWER_ID);
    const stat = getCur(stats, ELM_STAT_ID[el]);
    const doGen = getCur(stats, st.DAMAGE_ID);
    const doEl = getCur(stats, ELM_DMG_ID[el]);
    const doCrit = isCrit ? getCur(stats, st.CRIT_DAMAGE_ID) : 0;
    const fixed = doGen + doEl + doCrit;
    const inner = b + (b * (pwr + stat)) / 100 + fixed;
    return applySpellDmgFinalMult(inner, stats);
};

const applyWeaponDmgFinalMult = (raw, stats) => {
    const weaponPct = getCur(stats, WEAPON_PER_DMG_ID) || 0;
    const finalPct = getCur(stats, FINAL_PER_DMG_ID) || 0;
    const mult = (1 + weaponPct / 100) * (1 + finalPct / 100) * positionDamageMultiplier(stats);
    return Math.floor(toNum(raw) * mult);
};

const calcWeaponVal = ({ base, stats, el, isCrit }) => {
    const b = toNum(base);
    // Puissance (armes) is Power that only weapon hits may spend, which is why
    // it is added here and nowhere in `calcVal`. Nothing in the game gives it
    // on gear — it arrives from a boost (core/boosts.js), so on a build with no
    // boost selected this term is zero and the formula is what it always was.
    const pwr = getCur(stats, st.POWER_ID) + getCur(stats, st.WEAPON_POWER_ID);
    const stat = getCur(stats, ELM_STAT_ID[el]);
    const doGen = getCur(stats, st.DAMAGE_ID);
    const doEl = getCur(stats, ELM_DMG_ID[el]);
    const doCrit = isCrit ? getCur(stats, st.CRIT_DAMAGE_ID) : 0;
    const fixed = doGen + doEl + doCrit;
    const inner = b + (b * (pwr + stat)) / 100 + fixed;
    return applyWeaponDmgFinalMult(inner, stats);
};

const calcRange = (range, stats, el, isCrit) => {
    const min = hasVal(range?.min) ? calcVal({ base: range.min, stats, el, isCrit }) : null;
    const max = hasVal(range?.max) ? calcVal({ base: range.max, stats, el, isCrit }) : null;
    return { min, max };
};

/** Elements for “meilleur élément” damage/steal: earth+STR, fire+INT, water+CHANCE, air+AGI (+ per-element do). Neutral is excluded — only naturally neutral lines stay neutral. */
const BEST_ELEMENT_KEYS = ['earth', 'fire', 'water', 'air'];

/** Pick highest final damage among elements (same spell base min/max evaluated per element). */
const calcBestElementRange = (range, stats, isCrit) => {
    if (!range || (!hasVal(range.min) && !hasVal(range.max))) return { min: null, max: null };
    const minVals = BEST_ELEMENT_KEYS.map((el) =>
        (hasVal(range.min) ? calcVal({ base: range.min, stats, el, isCrit }) : null),
    ).filter((v) => v != null);
    const maxVals = BEST_ELEMENT_KEYS.map((el) =>
        (hasVal(range.max) ? calcVal({ base: range.max, stats, el, isCrit }) : null),
    ).filter((v) => v != null);
    return {
        min: minVals.length ? Math.max(...minVals) : null,
        max: maxVals.length ? Math.max(...maxVals) : null,
    };
};

/** Element key that maximizes final min+max for raw dice (icon for caster “best element” lines). */
const resolveBestElementForRawRange = (range, stats, isCrit) => {
    if (!range || (!hasVal(range.min) && !hasVal(range.max))) return BEST_ELEMENT_KEYS[0];
    let bestEl = BEST_ELEMENT_KEYS[0];
    let bestSum = -Infinity;
    for (const el of BEST_ELEMENT_KEYS) {
        let sum = 0;
        let n = 0;
        if (hasVal(range.min)) {
            sum += calcVal({ base: range.min, stats, el, isCrit });
            n++;
        }
        if (hasVal(range.max)) {
            sum += calcVal({ base: range.max, stats, el, isCrit });
            n++;
        }
        if (n === 0) continue;
        if (sum > bestSum) {
            bestSum = sum;
            bestEl = el;
        }
    }
    return bestEl;
};

const isCasterBestElementLevel = (lv) => lv?.damage_condition === 'caster_best_element';
const BEST_ELEMENT_STEAL_EFFECT_IDS = new Set([2828]);

const PUSH_DMG_ID = 84;

/** Push damage = (lvl/2 + do_pou + 32) * cells / 4. Resistances ignored. */
const calcPushDamage = (range, stats, charLvl) => {
    const lvl = toNum(charLvl);
    const doPou = getCur(stats, PUSH_DMG_ID) || 0;
    const base = lvl / 2 + doPou + 32;
    const min = hasVal(range?.min)
        ? applySpellDmgFinalMult(Math.floor((base * Number(range.min)) / 4), stats)
        : null;
    const max = hasVal(range?.max)
        ? applySpellDmgFinalMult(Math.floor((base * Number(range.max)) / 4), stats)
        : null;
    return { min, max };
};

// `weaponPower` is Puissance (armes): a weapon's heal lines spend it like the
// element stat, as its damage lines do (Maîtrise d'Arme raises a Marteau
// Possédé's heals in game). Spells pass nothing and heal as before.
const calcHealVal = ({ base, stats, el, effectId = null, weaponPower = 0 }) => {
    const b = toNum(base);
    if (isPercentMaxHpHealEffectId(effectId)) {
        const totalHp = getCur(stats, st.VITALITY_ID) || 0;
        return Math.max(0, Math.floor((totalHp * b) / 100));
    }
    const stat = getCur(stats, ELM_STAT_ID[el]) + weaponPower;
    const flatHeal = getCur(stats, HEAL_STAT_ID);
    const out = b * (1 + stat / 100) + flatHeal;
    return Math.max(0, Math.floor(out));
};

const calcHealRange = (range, stats, el, effectId = null, weaponPower = 0) => {
    const min = hasVal(range?.min) ? calcHealVal({ base: range.min, stats, el, effectId, weaponPower }) : null;
    const max = hasVal(range?.max) ? calcHealVal({ base: range.max, stats, el, effectId, weaponPower }) : null;
    return { min, max };
};

const calcBestElementHealRange = (range, stats) => {
    if (!range || (!hasVal(range.min) && !hasVal(range.max))) return { min: null, max: null };
    const minVals = BEST_ELEMENT_KEYS.map((el) =>
        (hasVal(range.min) ? calcHealVal({ base: range.min, stats, el }) : null),
    ).filter((v) => v != null);
    const maxVals = BEST_ELEMENT_KEYS.map((el) =>
        (hasVal(range.max) ? calcHealVal({ base: range.max, stats, el }) : null),
    ).filter((v) => v != null);
    return {
        min: minVals.length ? Math.max(...minVals) : null,
        max: maxVals.length ? Math.max(...maxVals) : null,
    };
};

const resolveBestHealElementForRawRange = (range, stats) => {
    if (!range || (!hasVal(range.min) && !hasVal(range.max))) return BEST_ELEMENT_KEYS[0];
    let bestEl = BEST_ELEMENT_KEYS[0];
    let bestSum = -Infinity;
    for (const el of BEST_ELEMENT_KEYS) {
        let sum = 0;
        let n = 0;
        if (hasVal(range.min)) {
            sum += calcHealVal({ base: range.min, stats, el });
            n++;
        }
        if (hasVal(range.max)) {
            sum += calcHealVal({ base: range.max, stats, el });
            n++;
        }
        if (n === 0) continue;
        if (sum > bestSum) {
            bestSum = sum;
            bestEl = el;
        }
    }
    return bestEl;
};

const avgFromRange = (r) => {
    if (r?.min != null && r?.max != null) return (r.min + r.max) / 2;
    if (r?.min != null) return r.min;
    if (r?.max != null) return r.max;
    return 0;
};

const maxFromRange = (r) => {
    if (r?.max != null) return r.max;
    if (r?.min != null) return r.min;
    return 0;
};

const BEST_ELEMENT_DAMAGE_EFFECT_IDS = new Set([2822, 2828]);

const scaledDamageNoFinalMult = ({ base, stats, el, isCrit }) => {
    const b = toNum(base);
    const pwr = getCur(stats, st.POWER_ID);
    const stat = getCur(stats, ELM_STAT_ID[el]);
    const doGen = getCur(stats, st.DAMAGE_ID);
    const doEl = getCur(stats, ELM_DMG_ID[el]);
    const doCrit = isCrit ? getCur(stats, st.CRIT_DAMAGE_ID) : 0;
    const inner = b + (b * (pwr + stat)) / 100 + doGen + doEl + doCrit;
    return Math.floor(inner);
};

const scaledBestElementNoFinalMult = ({ base, stats, isCrit }) =>
    Math.max(...BEST_ELEMENT_KEYS.map((el) => scaledDamageNoFinalMult({ base, stats, el, isCrit })));

const spellDamageMultiplier = (stats) => {
    const spellPct = getCur(stats, st.SPELL_PER_DMG_ID) || 0;
    const finalPct = getCur(stats, FINAL_PER_DMG_ID) || 0;
    return (1 + spellPct / 100) * (1 + finalPct / 100) * positionDamageMultiplier(stats);
};

/** Expected value per line (crit-weighted). critPct 0-100. */
export const calcLineExpectedVal = (line, critPct, stats = null) => {
    const p = Math.min(100, Math.max(0, toNum(critPct))) / 100;
    if (stats && line?.kind === 'damage' && line?.baseRaw && line?.el !== 'push') {
        const isBestElement = BEST_ELEMENT_DAMAGE_EFFECT_IDS.has(Number(line?.effectId));
        const rawMin = hasVal(line.baseRaw?.min) ? Number(line.baseRaw.min) : Number(line.baseRaw?.max);
        const rawMax = hasVal(line.baseRaw?.max) ? Number(line.baseRaw.max) : Number(line.baseRaw?.min);
        if (Number.isFinite(rawMin) || Number.isFinite(rawMax)) {
            const bMin = Number.isFinite(rawMin) ? rawMin : rawMax;
            const bMax = Number.isFinite(rawMax) ? rawMax : rawMin;
            const sbLo = isBestElement
                ? scaledBestElementNoFinalMult({ base: bMin, stats, isCrit: false })
                : scaledDamageNoFinalMult({ base: bMin, stats, el: line.el, isCrit: false });
            const sbHi = isBestElement
                ? scaledBestElementNoFinalMult({ base: bMax, stats, isCrit: false })
                : scaledDamageNoFinalMult({ base: bMax, stats, el: line.el, isCrit: false });
            const avgBase = (sbLo + sbHi) / 2.0;
            let ev = Math.round(avgBase);
            if (line?.critRaw && (hasVal(line.critRaw.min) || hasVal(line.critRaw.max))) {
                const cMinRaw = hasVal(line.critRaw.min) ? Number(line.critRaw.min) : Number(line.critRaw.max);
                const cMaxRaw = hasVal(line.critRaw.max) ? Number(line.critRaw.max) : Number(line.critRaw.min);
                const cMin = Number.isFinite(cMinRaw) ? cMinRaw : cMaxRaw;
                const cMax = Number.isFinite(cMaxRaw) ? cMaxRaw : cMinRaw;
                const scLo = isBestElement
                    ? scaledBestElementNoFinalMult({ base: cMin, stats, isCrit: true })
                    : scaledDamageNoFinalMult({ base: cMin, stats, el: line.el, isCrit: true });
                const scHi = isBestElement
                    ? scaledBestElementNoFinalMult({ base: cMax, stats, isCrit: true })
                    : scaledDamageNoFinalMult({ base: cMax, stats, el: line.el, isCrit: true });
                const avgCrit = (scLo + scHi) / 2.0;
                ev = Math.round((1 - p) * avgBase + p * avgCrit);
            }
            return ev * spellDamageMultiplier(stats);
        }
    }
    const useMax = line?.el === 'push';
    const avgBase = useMax ? maxFromRange(line.base) : avgFromRange(line.base);
    if (!line.crit || (line.crit.min == null && line.crit.max == null)) return Math.round(avgBase);
    const avgCrit = useMax ? maxFromRange(line.crit) : avgFromRange(line.crit);
    const out = (1 - p) * avgBase + p * avgCrit;
    return Math.round(out);
};

const DIRECT_LBL = { fr: 'Direct', en: 'Direct', de: 'Direkt', es: 'Directo', pt: 'Direto' };

export const formatSpellLineConditionText = ({
    line,
    blockLabel = null,
    lang = 'fr',
    healLabel = null,
    fallback = 'none',
    suffix = null,
} = {}) => {
    if (!line) return null;
    const l = TXT_EN[lang] || TXT_EN.en;
    let base = null;
    if (line.kind === 'base_damage_bonus' && line.bonusLabel) {
        base = line.bonusLabel;
    } else {
        const specs = collectTargetUnderStateSpecs(line);
        const inlineStateLabel = specs.length > 0
            ? (hasSadidaInfectionStateBundle(specs)
                ? sadidaInfectionLabel(lang)
                : collapseStateSpecsForLabel(specs, lang)
                    .map((s) => fmtTargetUnderStateSpec(s, lang))
                    .filter(Boolean)
                    .join(' · '))
            : null;
        const condParts = [
            line.stackRowCaption,
            blockLabel,
            line.condition,
            inlineStateLabel,
            line.targetLabel,
        ].filter(Boolean);
        if (condParts.length > 0) {
            base = condParts.join(' · ');
        } else if (line.kind === 'heal') {
            base = healLabel || HEAL_LBL[l] || HEAL_LBL.en;
        } else if (fallback === 'direct') {
            base = line?.exclusiveGroupKey
                ? (CLIENT_ONLY_LBL[l] || CLIENT_ONLY_LBL.en)
                : (DIRECT_LBL[l] || DIRECT_LBL.en);
        }
    }
    if (!base) return suffix || null;
    return suffix ? `${base} · ${suffix}` : base;
};

export const isDirectConditionLabel = (condText) => {
    const txt = String(condText || '').trim().toLowerCase();
    return txt === 'direct' || txt.startsWith('direct ·') || txt.startsWith('direct •');
};

export const sortRowsByConditionDisplayOrder = (rows) =>
    (rows || [])
        .map((row, idx) => ({ row, idx }))
        .sort((a, b) => {
            const ar = isDirectConditionLabel(a?.row?.condText) ? 0 : 1;
            const br = isDirectConditionLabel(b?.row?.condText) ? 0 : 1;
            if (ar !== br) return ar - br;
            return a.idx - b.idx;
        })
        .map((entry) => entry.row);

const isHealSpellLine = (line) => line?.kind === 'heal';
export const isAllyOnlyDamageLine = (line) =>
    line?.kind === 'damage' && line?.affectsAllies === true && line?.affectsEnemies === false;

/** Strip lines that should never appear in any view, so card and details stay in sync. */
export const filterSpellLinesForDisplay = (spell) => {
    const filterLines = (lines) => (lines || []).filter((ln) => !isAllyOnlyDamageLine(ln));
    return {
        ...spell,
        baseLines: filterLines(spell.baseLines),
        conditionalBlocks: (spell.conditionalBlocks || []).map((block) => ({
            ...block,
            lines: filterLines(block.lines),
        })),
        bonusLines: filterLines(spell.bonusLines),
        summonChildRows: (spell.summonChildRows || []).map((cr) => ({
            ...cr,
            baseLines: filterLines(cr.baseLines),
            conditionalBlocks: (cr.conditionalBlocks || []).map((b) => ({
                ...b,
                lines: filterLines(b.lines),
            })),
            bonusLines: filterLines(cr.bonusLines),
        })),
    };
};

const countsTowardDmgSummary = (l) => {
    if (l.kind !== 'damage') return false;
    // clientOnly no longer disqualifies a line: duplicates are removed at the raw rows
    // (dropDuplicatedClientOnlyRows), and what remains is the only carrier of its value.
    if (l.clientOnlyProxy) return false;
    if (l.summonOnlyDmg || l.mpBonusDmg) return false;
    const si = l.stackIndex;
    if (si != null && Number.isFinite(si) && si !== 0) return false;
    return true;
};

const totalAvgDmg = (lines) => {
    let sum = 0;
    const bestByGroup = {};
    for (const l of lines || []) {
        if (!countsTowardDmgSummary(l)) continue;
        const g = l.exclusiveGroupKey || l.elVariantXorKey;
        const v = avgFromRange(l.base);
        if (!g) {
            sum += v;
            continue;
        }
        bestByGroup[g] = bestByGroup[g] == null ? v : Math.max(bestByGroup[g], v);
    }
    return sum + Object.values(bestByGroup).reduce((a, b) => a + b, 0);
};

/**
 * First-line total shown on spell cards (matches SpellView).
 * Eliotrope: base non-heal + through_portal block non-heal. Else: base non-heal only.
 */
const includeInPrimaryDmgTotal = (line, suppressAllyOnly = false) => {
    if (isHealSpellLine(line)) return false;
    if (suppressAllyOnly && isAllyOnlyDamageLine(line)) return false;
    if (line.kind === 'base_damage_bonus') return false;
    if (line.clientOnlyProxy) return false;
    if (line.summonOnlyDmg || line.mpBonusDmg) return false;
    if (line.isEmbedded) return true;
    const si = line.stackIndex;
    if (si != null && Number.isFinite(si) && si !== 0) return false;
    return true;
};

/**
 * The `target_under_state` gate that bucketed this line, wherever it now lives.
 *
 * Bucketing moves the gate off the line and onto its conditional block, so the
 * totals below used to fall through to the raw target mask — where "with the
 * state" and "without it" differ by letter case alone (`*E2483` / `*e2483`), and
 * the fallback lowercases. Both Roublard wall branches then looked like one
 * branch and were summed. `stateGate` is stamped back on at map time for the same
 * reason `portalGate` is; see calcSpellRows.
 */
const stateGateSpecsOf = (line) => {
    if (Array.isArray(line?.stateGate) && line.stateGate.length > 0) return line.stateGate;
    return (line?.conditions || []).filter((c) => c?.id === 'target_under_state');
};

const stateConditionBranchKey = (line) => {
    const specs = stateGateSpecsOf(line)
        .map((c) => {
            const sid = c?.state_id ?? c?.stateId;
            if (sid == null || sid === '') return null;
            const neg = c?.state_negated ?? c?.stateNegated;
            const tok = c?.state_token_case ?? c?.stateTokenCase ?? '';
            return `${sid}:${neg === true ? 1 : 0}:${tok}`;
        })
        .filter(Boolean);
    if (specs.length) return specs.join('|');
    const fallback = String(
        line?.targetLabel
        || line?.condition
        || line?.stackRowCaption
        || line?.targetMask
        || ''
    ).trim();
    if (!fallback) return null;
    return `label:${fallback}`;
};

const primaryLineVariantGroupKey = (line) => {
    if (line?.kind !== 'damage' || line?.el === 'push') return null;
    if (line?.exclusiveGroupKey) return `xor:${line.exclusiveGroupKey}`;
    // Element-variant lines share one XOR group for damage totals (cast in one
    // element only). Keyed on elVariantXorKey, not per-element, so all variants
    // collapse to the best single element. Display grouping uses other helpers,
    // so this stays a total-only concern.
    if (line?.elVariantXorKey) return `xor:${line.elVariantXorKey}`;
    const effectId = line?.effectId;
    if (effectId != null && effectId !== '') {
        return `effect:${effectId}:${line.el}:${line.isLifesteal ? 1 : 0}`;
    }
    return null;
};

const primaryVariantFamilyKey = (line) => {
    if (!line || line.kind !== 'damage' || line.el === 'push') return null;
    const effectId = line?.effectId;
    if (effectId == null || effectId === '') return null;
    const trig = line?.triggers ?? 'I';
    const delay = Number.isFinite(Number(line?.delay)) ? Number(line.delay) : 0;
    const si = Number.isFinite(Number(line?.stackIndex)) ? Number(line.stackIndex) : 'x';
    const sm = Number.isFinite(Number(line?.stackMax)) ? Number(line.stackMax) : 'x';
    return [
        effectId,
        line.el,
        line.isLifesteal ? 1 : 0,
        trig,
        delay,
        si,
        sm,
    ].join('|');
};

const primaryConditionSig = (line) => {
    const stateCond = JSON.stringify(
        stateGateSpecsOf(line)
            .map((c) => ({
                sid: c?.state_id ?? c?.stateId ?? null,
                neg: c?.state_negated ?? c?.stateNegated ?? null,
                tok: c?.state_token_case ?? c?.stateTokenCase ?? null,
            })),
    );
    return [
        String(line?.condition || '').trim().toLowerCase(),
        String(line?.targetLabel || '').trim().toLowerCase(),
        String(line?.stackRowCaption || '').trim().toLowerCase(),
        String(line?.targetMask || '').trim(),
        stateCond,
    ].join('|');
};

const isSameTickPrimaryLine = (line) =>
    (line?.triggers == null || line.triggers === 'I')
    && (!Number.isFinite(Number(line?.delay)) || Number(line.delay) === 0);

/** True when the spell has “direct” primary base damage (not nested-glyph rows). */
export const hasNonEmbeddedPrimaryBaseDamage = (spell) =>
    (spell.baseLines || []).some(
        (line) => line.kind === 'damage' && !line.isEmbedded && includeInPrimaryDmgTotal(line),
    );

/** True when baseLines has any qualifying primary damage (embedded or not). */
export const spellHasPrimaryBaseDamage = (spell) =>
    (spell.baseLines || []).some((line) => line.kind === 'damage' && includeInPrimaryDmgTotal(line));

const hasPromotedConditionalPrimaryDamage = (spell, eliotropePortalCardOnly = false) => {
    const timedCondKeys = new Set(['start_of_turn', 'end_of_turn']);
    const hasTimedBlock = (spell?.conditionalBlocks || []).some((block) => timedCondKeys.has(block?.condKey));
    const promotedCondKeys = hasTimedBlock
        ? (spell?.conditionalBlocks || [])
            .map((block) => block?.condKey)
            .filter((key) => timedCondKeys.has(key))
        : (spell?.conditionalBlocks || [])
            .map((block) => block?.condKey)
            .filter((key) => !(eliotropePortalCardOnly && key === 'through_portal'));
    if (!promotedCondKeys.length) return false;
    return (spell?.conditionalBlocks || []).some((block) => (
        promotedCondKeys.includes(block?.condKey)
        && (block?.lines || []).some((line) => line?.kind === 'damage' && includeInPrimaryDmgTotal(line))
    ));
};

/** True when card header can show a primary damage line (base or promoted conditional). */
export const spellHasDisplayedPrimaryDamage = (spell, eliotropePortalCardOnly = false) =>
    spellHasPrimaryBaseDamage(spell) || hasPromotedConditionalPrimaryDamage(spell, eliotropePortalCardOnly);

const isChargeTierDamage = (line) =>
    line.kind === 'damage'
    && line.el !== 'push'
    && !isHealSpellLine(line)
    && line.stackIndex != null
    && Number.isFinite(line.stackIndex)
    && line.stackIndex >= 0;

const chargeTierGe1 = (line) => isChargeTierDamage(line) && line.stackIndex >= 1;

/** Charge stacking (293): tier ≥ 1 may live on timed/conditional rows (e.g. Colère delay), not only baseLines. */
export const spellHasChargeStacking = (spell) => {
    if ((spell.baseLines || []).some(chargeTierGe1)) return true;
    for (const b of spell.conditionalBlocks || []) {
        if ((b.lines || []).some(chargeTierGe1)) return true;
    }
    return false;
};

export const spellHasMpPeakBonus = (spell) => {
    if ((spell.baseLines || []).some((l) => l.mpBonusDmg)) return true;
    for (const b of spell.conditionalBlocks || []) {
        if ((b.lines || []).some((l) => l.mpBonusDmg)) return true;
    }
    return false;
};

const collectMpBonusEvs = (spell, eliotropePortalCardOnly, ev) => {
    const vals = [];
    for (const line of spell.baseLines || []) {
        if (line.kind === 'damage' && line.el !== 'push' && !isHealSpellLine(line) && line.mpBonusDmg) {
            vals.push(ev(line));
        }
    }
    for (const block of spell.conditionalBlocks || []) {
        if (eliotropePortalCardOnly && block.condKey !== 'through_portal') continue;
        for (const line of block.lines || []) {
            if (line.kind === 'damage' && line.el !== 'push' && !isHealSpellLine(line) && line.mpBonusDmg) {
                vals.push(ev(line));
            }
        }
    }
    return vals;
};

const collectChargeTierEvs = (spell, eliotropePortalCardOnly, ev) => {
    const vals = [];
    for (const line of spell.baseLines || []) {
        if (isChargeTierDamage(line)) vals.push(ev(line));
    }
    for (const block of spell.conditionalBlocks || []) {
        if (eliotropePortalCardOnly && block.condKey !== 'through_portal') continue;
        for (const line of block.lines || []) {
            if (isChargeTierDamage(line)) vals.push(ev(line));
        }
    }
    return vals;
};

/**
 * Group lines that are one hit written more than once.
 *
 * Two separate relations say rows are alternatives: Dofus's own
 * `exclusive_branch_key` (and the element-variant key beside it), and "same damage
 * profile under a different gate". A line can sit in BOTH, so keying each line on
 * one relation and dropping the other tears a single hit into two groups.
 *
 * Oblitération split exactly that way: its enemy row pairs with the bomb row by
 * profile, the bomb row pairs with the charge-tier row by exclusive_branch_key, and
 * the two pairs never met — the card totalled 624 for a cast that lands once, and
 * drew an element icon per pair. So the relations are unioned transitively.
 *
 * The card's total and its header icons both read this, and must: a number that
 * counts one hit beside two icons saying two is the same bug told twice.
 *
 * Returns components in first-appearance order, each flagged with whether a gate
 * difference (rather than only an exclusive_branch_key) holds it together, which is
 * what decides how the total reduces it.
 */
export const primaryAlternativeComponents = (lines) => {
    const kept = (lines || []).filter(Boolean);
    if (!kept.length) return [];

    const parent = new Map(kept.map((line) => [line, line]));
    const find = (line) => {
        let root = line;
        while (parent.get(root) !== root) root = parent.get(root);
        let walk = line;
        while (parent.get(walk) !== root) {
            const next = parent.get(walk);
            parent.set(walk, root);
            walk = next;
        }
        return root;
    };
    const union = (a, b) => {
        const ra = find(a);
        const rb = find(b);
        if (ra !== rb) parent.set(ra, rb);
    };
    const linkByKey = (keyOf) => {
        const firstWithKey = new Map();
        for (const line of kept) {
            const key = keyOf(line);
            if (!key) continue;
            const seen = firstWithKey.get(key);
            if (seen) union(seen, line);
            else firstWithKey.set(key, line);
        }
    };

    linkByKey(primaryLineVariantGroupKey);

    // Same damage profile under two different gates is one hit written twice. A
    // family that never varies its gate is repeated hits (Cerbère lands three
    // times under Forme Bestiale) and has to stay additive.
    const inForcedFamily = new Set();
    const families = new Map();
    for (const line of kept) {
        const fk = primaryVariantFamilyKey(line);
        if (!fk) continue;
        if (!families.has(fk)) families.set(fk, []);
        families.get(fk).push(line);
    }
    families.forEach((famLines) => {
        if (famLines.length < 2) return;
        const condSigs = new Set(famLines.map((line) => primaryConditionSig(line)).filter(Boolean));
        if (condSigs.size < 2) return;
        famLines.forEach((line) => inForcedFamily.add(line));
        famLines.slice(1).forEach((line) => union(famLines[0], line));
    });

    // A line with no alternative is a component of one, so it stands for itself.
    const byRoot = new Map();
    const order = [];
    for (const line of kept) {
        const root = find(line);
        if (!byRoot.has(root)) {
            byRoot.set(root, { lines: [], fromGateVariant: false });
            order.push(root);
        }
        const comp = byRoot.get(root);
        comp.lines.push(line);
        if (inForcedFamily.has(line)) comp.fromGateVariant = true;
    }
    return order.map((root) => byRoot.get(root));
};

/**
 * A row's own caption keeps it apart from a row that merely shares its numbers —
 * Ydra's hit and its "Déclenché" twin deal the same earth damage and are not the
 * same event. The exception is a charge row's base tier: "Base" at zero charges IS
 * the plain hit, so it may fold into one written without a caption.
 */
const captionForSignature = (line) => {
    // Number(null) is 0, so test the value itself — a line with no stack index at all
    // must not read as the base tier.
    const si = line?.stackIndex;
    if (Number.isFinite(si) && si === 0) return '';
    return String(line?.stackRowCaption || '').trim().toLowerCase();
};

const alternativeRowSignature = (line) => {
    const range = (r) => `${r?.min ?? 'x'}-${r?.max ?? 'x'}`;
    return [
        line?.kind || '',
        line?.el || '',
        line?.isLifesteal ? 1 : 0,
        range(line?.base),
        range(line?.crit),
        line?.triggers ?? 'I',
        Number(line?.delay) || 0,
        captionForSignature(line),
    ].join(':');
};

/**
 * Any gate at all — a spell state, a portal branch, or a condition the row already
 * spells out. A gated row earns its place even when its numbers match its sibling's:
 * Audace deals the same water hit through a portal or not, and saying so is the point.
 */
const carriesGate = (line) => (
    stateGateSpecsOf(line).length > 0
    || !!line?.portalGate
    || !!String(line?.condition || '').trim()
    || !!String(line?.targetLabel || '').trim()
);
// A charge caption is deliberately not a gate: tiers that differ are already held
// apart by their numbers, so the only "Base" row this can fold away is one whose
// numbers match the plain hit — which is the plain hit.

/**
 * Drop rows that are one hit written twice and say nothing new.
 *
 * Resquille lands once, but Dofus writes it three times — once per target category
 * (`A`, `g,p`, `g,P,f3112…`) — with identical numbers on all three. The card and the
 * details table both listed every row, and labelled them "Direct" and "Conditionnel",
 * a distinction the game does not have.
 *
 * Only rows in one component, with identical numbers, and carrying NO state gate are
 * folded — and a mask repeated N times keeps its N rows. So a spell that really lands
 * more than once keeps every row (Cerbère hits three times under Forme Bestiale), and
 * a gated branch stays visible even when its numbers happen to match its sibling
 * (Mur de Feu's "Avec"/"Sans Tour Roublard").
 */
export const dedupeIdenticalAlternatives = (lines) => {
    const list = (lines || []).filter(Boolean);
    if (list.length < 2) return list;
    const componentOf = new Map();
    primaryAlternativeComponents(list).forEach((comp, idx) => {
        comp.lines.forEach((line) => componentOf.set(line, idx));
    });
    const bucketKey = (line) => {
        const comp = componentOf.get(line);
        if (comp == null || carriesGate(line)) return null;
        return `${comp}|${alternativeRowSignature(line)}`;
    };
    const buckets = new Map();
    for (const line of list) {
        const key = bucketKey(line);
        if (!key) continue;
        if (!buckets.has(key)) buckets.set(key, []);
        buckets.get(key).push(line);
    }
    const keepCount = new Map();
    buckets.forEach((bucketLines, key) => {
        if (bucketLines.some(carriesGate)) {
            keepCount.set(key, bucketLines.length);
            return;
        }
        const perMask = new Map();
        for (const line of bucketLines) {
            const mask = line?.targetMask || '';
            perMask.set(mask, (perMask.get(mask) || 0) + 1);
        }
        keepCount.set(key, Math.max(...perMask.values()));
    });
    const used = new Map();
    return list.filter((line) => {
        const key = bucketKey(line);
        if (!key) return true;
        const n = (used.get(key) || 0) + 1;
        used.set(key, n);
        return n <= (keepCount.get(key) ?? 1);
    });
};

/**
 * Total a line set, one value per component.
 *
 * Gate variants of one hit are worth their best branch; a component held together
 * only by an exclusive_branch_key keeps its first row, because there that key spans
 * the charge tiers of a single row (base / "4 charges") and the card already carries
 * the raised tiers as rows of their own.
 */
const sumPrimaryLinesWithAlternatives = (lines, ev, suppressAllyOnly = false) => {
    const kept = (lines || []).filter((line) => includeInPrimaryDmgTotal(line, suppressAllyOnly));
    let sum = 0;
    for (const comp of primaryAlternativeComponents(kept)) {
        const vals = comp.lines.map((line) => ev(line));
        sum += comp.fromGateVariant ? Math.max(...vals) : vals[0];
    }
    return sum;
};

// One cast goes through a portal or it does not, so `direct` and `through_portal`
// lines are branches of the same hit and must never be added together.
const PORTAL_GATE_KEYS = ['through_portal', 'direct'];

// The gate itself is stripped off the line when it is bucketed into its conditional
// block, so it is stamped back on at map time (see calcSpellRows) to stay readable here.
const portalGateOf = (line) => line?.portalGate || null;

/** Ungated damage plus the best portal branch — the gates are exclusive, not additive. */
const sumPrimaryAcrossPortalGates = (lines, ev, suppressAllyOnly = false) => {
    const ungated = [];
    const gated = new Map();
    for (const line of lines || []) {
        const gate = portalGateOf(line);
        if (!gate) {
            ungated.push(line);
            continue;
        }
        if (!gated.has(gate)) gated.set(gate, []);
        gated.get(gate).push(line);
    }
    const total = sumPrimaryLinesWithAlternatives(ungated, ev, suppressAllyOnly);
    if (!gated.size) return total;
    const gateTotals = Array.from(gated.values(), (g) => sumPrimaryLinesWithAlternatives(g, ev, suppressAllyOnly));
    return total + Math.max(...gateTotals);
};

const collectPrimaryCandidateDamageLines = (spell, eliotropePortalCardOnly, suppressAllyOnly = false) => {
    const out = [];
    for (const line of spell.baseLines || []) {
        if (includeInPrimaryDmgTotal(line, suppressAllyOnly)) out.push(line);
    }
    if (eliotropePortalCardOnly) {
        for (const block of spell.conditionalBlocks || []) {
            if (block.condKey !== 'through_portal') continue;
            for (const line of block.lines || []) {
                if (!isSameTickPrimaryLine(line)) continue;
                if (includeInPrimaryDmgTotal(line, suppressAllyOnly)) out.push(line);
            }
        }
    }
    if (!spellHasPrimaryBaseDamage(spell)) {
        for (const block of spell.conditionalBlocks || []) {
            if (eliotropePortalCardOnly && block.condKey === 'through_portal') continue;
            for (const line of block.lines || []) {
                if (line.kind === 'damage' && includeInPrimaryDmgTotal(line, suppressAllyOnly)) out.push(line);
            }
        }
    }
    return out;
};

/**
 * Total one branch's lines.
 *
 * Inside a branch, repeated rows are repeated *hits* — Cerbère really lands three
 * times under Forme Bestiale, and the card is meant to show all three. So lines add
 * up, and only rows carrying an explicit exclusivity marker collapse: those are one
 * hit written twice, like the wall's `a,p,…` / `a,P,f3112,…` pair, which Dofus tags
 * with a shared exclusive_branch_key.
 *
 * Do NOT reach for sumPrimaryLinesWithAlternatives here. Its last-resort grouping
 * keys on effect id alone, which reads three identical Forme Bestiale rows as one
 * row said three times and drops two real hits.
 */
const sumBranchLines = (lines, ev) => {
    let sum = 0;
    const bestByExclusiveKey = new Map();
    for (const line of lines || []) {
        const xorKey = line?.exclusiveGroupKey || line?.elVariantXorKey || null;
        const v = ev(line);
        if (!xorKey) {
            sum += v;
            continue;
        }
        const seen = bestByExclusiveKey.get(xorKey);
        bestByExclusiveKey.set(xorKey, seen == null ? v : Math.max(seen, v));
    }
    for (const v of bestByExclusiveKey.values()) sum += v;
    return sum;
};

const calcCasterBestStateBranchPrimaryTotal = (spell, eliotropePortalCardOnly, ev, suppressAllyOnly = false) => {
    if (!['caster_best_element', 'target_under_state'].includes(spell?.damageCondition)) return null;
    const lines = collectPrimaryCandidateDamageLines(spell, eliotropePortalCardOnly, suppressAllyOnly)
        .filter((line) => line?.kind === 'damage' && includeInPrimaryDmgTotal(line, suppressAllyOnly));
    if (!lines.length) return null;
    const branches = new Map();
    const ungated = [];
    for (const line of lines) {
        const bk = stateConditionBranchKey(line);
        if (!bk) {
            ungated.push(line);
            continue;
        }
        if (!branches.has(bk)) branches.set(bk, []);
        branches.get(bk).push(line);
    }
    if (branches.size < 2) return null;
    const branchTotals = Array.from(branches.values(), (group) => sumBranchLines(group, ev));
    return sumBranchLines(ungated, ev) + Math.max(...branchTotals);
};

const calcAllRandomPrimaryExpectedTotal = (spell, eliotropePortalCardOnly, ev, suppressAllyOnly = false) => {
    const lines = collectPrimaryCandidateDamageLines(spell, eliotropePortalCardOnly, suppressAllyOnly);
    if (!lines || lines.length < 2) return null;
    let wSum = 0;
    let weighted = 0;
    for (const line of lines) {
        const w = Number(line?.randomChance);
        if (!Number.isFinite(w) || w <= 0) return null;
        wSum += w;
        weighted += w * ev(line);
    }
    if (!(wSum > 0)) return null;
    return Math.round(weighted / wSum);
};

/** Compact card header: tier‑0 / non‑charge damage only (sub‑rows show charge tiers). */
const calcSpellCardPrimaryHeaderTotal = (spell, eliotropePortalCardOnly) => {
    const critPct = spell.critPct ?? 0;
    const ev = (line) => calcLineExpectedVal(line, critPct);
    const suppressAllyOnly = true;
    let sum;
    const casterBestStateBranchTotal = calcCasterBestStateBranchPrimaryTotal(spell, eliotropePortalCardOnly, ev, suppressAllyOnly);
    if (casterBestStateBranchTotal != null) {
        sum = casterBestStateBranchTotal;
    } else {
        // Same candidate set the state-branch and random totals already use, summed in
        // one pass so a `direct`/`through_portal` pair can cancel across the buckets it
        // used to be split into (Audace was showing 2x one branch).
        sum = sumPrimaryAcrossPortalGates(
            collectPrimaryCandidateDamageLines(spell, eliotropePortalCardOnly, suppressAllyOnly),
            ev,
            suppressAllyOnly,
        );
    }
    const randomExpected = calcAllRandomPrimaryExpectedTotal(spell, eliotropePortalCardOnly, ev, suppressAllyOnly);
    if (randomExpected != null) return randomExpected;
    return sum;
};

export const calcSpellCardPrimaryTotal = (spell, eliotropePortalCardOnly) => {
    const critPct = spell.critPct ?? 0;
    const ev = (line) => calcLineExpectedVal(line, critPct);
    let best = null;

    if (spellHasChargeStacking(spell)) {
        const vals = collectChargeTierEvs(spell, eliotropePortalCardOnly, ev);
        if (vals.length > 0) best = Math.max(...vals);
    }
    if (best == null) {
        best = calcSpellCardPrimaryHeaderTotal(spell, eliotropePortalCardOnly);
    }
    if (spellHasMpPeakBonus(spell)) {
        const mps = collectMpBonusEvs(spell, eliotropePortalCardOnly, ev);
        if (mps.length > 0) best = Math.max(best, ...mps);
    }
    return best;
};

const fmtBaseDamageBonusLabel = ({
    spellName, min, max, delay, duration, lang,
}) => {
    const l = TXT_EN[lang] || TXT_EN.en;
    const lo = toNum(min);
    const hi = toNum(max);
    const flat = lo === hi ? `+${lo}` : `+${lo}–${hi}`;
    const dmg = l === 'fr' ? 'dégâts de base' : 'base damage';
    let s = `${spellName} : ${flat} ${dmg}`;
    const d = toNum(duration);
    const del = toNum(delay);
    if (l === 'fr') {
        if (d > 0) s += ` (dans ${d} tour${d > 1 ? 's' : ''})`;
        if (del > 0) s += ` (${del} tour${del > 1 ? 's' : ''})`;
    } else {
        if (d > 0) s += ` (over ${d} turn${d > 1 ? 's' : ''})`;
        if (del > 0) s += ` (${del} turn${del > 1 ? 's' : ''})`;
    }
    return s;
};

const mapLine = (line, lv, stats, lang, charLvl, mapOpts = {}) => {
    const {
        dropTargetUnderStateLineHint = false,
        spellId = null,
        clientOnlyElementFallback = true,
    } = mapOpts;
    if (line.el === 'push') {
        return {
            kind: 'damage',
            el: 'push',
            baseRaw: line.base,
            critRaw: null,
            base: calcPushDamage(line.base, stats, charLvl),
            crit: null,
            condition: null,
            targetLabel: null,
            triggers: line.triggers ?? 'I',
            delay: line.delay ?? 0,
            elVariantXorKey: line.elVariantXorKey ?? null,
            sourceKeys: line.sourceKeys || [],
        };
    }
    if (line.kind === 'heal') {
        const targetLbl = line.affectsAllies ? (COND_REGISTRY.allies[(TXT_EN[lang] || TXT_EN.en)]) : null;
        const bestHeal = isBestElementHealEffectId(line.effectId);
        const elKey = bestHeal ? resolveBestHealElementForRawRange(line.base, stats) : line.el;
        return {
            kind: 'heal',
            el: elKey,
            effectId: line.effectId ?? null,
            baseRaw: line.base,
            critRaw: line.crit,
            base: bestHeal
                ? calcBestElementHealRange(line.base, stats)
                : calcHealRange(line.base, stats, line.el, line.effectId ?? null),
            crit: line.crit != null
                ? (bestHeal
                    ? calcBestElementHealRange(line.crit, stats)
                    : calcHealRange(line.crit, stats, line.el, line.effectId ?? null))
                : null,
            condition: null,
            targetLabel: targetLbl,
            targetMask: line.targetMask ?? null,
            randomChance: line.randomChance ?? null,
            randomGroup: line.randomGroup ?? null,
            triggers: line.triggers ?? 'I',
            delay: line.delay ?? 0,
            elVariantXorKey: line.elVariantXorKey ?? null,
            sourceKeys: line.sourceKeys || [],
        };
    }
    const l = TXT_EN[lang] || TXT_EN.en;
    const allyEnemy = line.affectsAllies && line.affectsEnemies;
    const baseTargetLbl = line.affectsAllies && !line.affectsEnemies ? COND_REGISTRY.allies[l] : null;
    const targetLbl = [baseTargetLbl, maskPredicateBranchLabel(line.targetMask, lang)]
        .filter(Boolean)
        .join(' · ') || null;
    const bestEl = isCasterBestElementLevel(lv) || (line != null && (Number(line.effectId) === 2822 || Number(line.effectId) === 2828));
    const bestElementSteal = bestEl && BEST_ELEMENT_STEAL_EFFECT_IDS.has(Number(line.effectId));
    const cond = bestElementSteal
        ? COND_REGISTRY.best_element_steal[l]
        : line.isLifesteal
        ? (bestEl ? COND_REGISTRY.best_element_steal[l] : COND_REGISTRY.health_steal[l])
        : (bestEl ? (COND_LBL.caster_best_element?.[l] || null) : fmtCond(lv, lang, line.el, { dropTargetUnderStateLineHint }));
    const elKey = bestEl ? resolveBestElementForRawRange(line.base, stats, false) : line.el;
    const baseR = bestEl
        ? calcBestElementRange(line.base, stats, false)
        : calcRange(line.base, stats, line.el, false);
    const critR = line.crit != null
        ? (bestEl
            ? calcBestElementRange(line.crit, stats, true)
            : calcRange(line.crit, stats, line.el, true))
        : null;
    const tm = line.targetMask ?? null;
    const summonOnlyDmg = isSummonOnlyTargetMask(tm);
    const mpBonusDmg = isMpRemainderEffectId(line.effectId);
    const clientOnly = line.forClientOnly === true;
    let stackRowCaption;
    const explicitRowCaption = stripMaximumPrefix(pickI18n(line.rowCaption, lang));
    const carrierDesc = line.embeddingCarrierDescription
        ? stripMaximumPrefix(pickI18n(line.embeddingCarrierDescription, lang))
        : null;
    if (explicitRowCaption) {
        stackRowCaption = explicitRowCaption;
    } else if (carrierDesc) {
        const stateNames = line.embeddingConditionStateNames;
        const stateStr = hasSadidaInfectionNameBundle(stateNames)
            ? sadidaInfectionLabel(lang)
            : Array.isArray(stateNames) && stateNames.length
            ? stateNames.map((s) => pickI18n(s, lang) || String(s)).filter(Boolean).join(' · ')
            : null;
        stackRowCaption = stateStr || carrierDesc;
    } else {
        const isImmediate = rawEffectTrigger(line) === 'I' && lineDelay(line) === 0;
        if (!stackRowCaption && clientOnly && isImmediate && clientOnlyElementFallback) {
            stackRowCaption = EL_NAMES[line.el]?.[l] ?? CLIENT_ONLY_LBL[l];
        }
        if (!stackRowCaption && summonOnlyDmg) stackRowCaption = SUR_INVOC_LBL[l];
        if (!stackRowCaption && line.stackIndex != null && Number.isFinite(line.stackIndex)) {
            if (line.stackIndex === 0 && allyEnemy) stackRowCaption = ALLY_ENEMY_LBL[l];
            else if (line.stackIndex >= 1) stackRowCaption = fallbackChargeCaption(line.stackIndex, lang);
        }
    }
    return {
        kind: 'damage',
        el: elKey,
        // Kept on the rendered row: the icon is a lifesteal one, and `condition`
        // alone is a translated string the renderer cannot key off.
        isLifesteal: line.isLifesteal ?? false,
        effectId: line.effectId ?? null,
        elVariantXorKey: line.elVariantXorKey ?? null,
        targetMask: tm,
        embeddedFromSpellId: line.embeddedFromSpellId ?? null,
        embeddingCarrierEffectId: line.embeddingCarrierEffectId ?? null,
        baseRaw: line.base,
        critRaw: line.crit,
        base: baseR,
        crit: critR,
        conditions: Array.isArray(line.conditions) ? line.conditions : [],
        condition: cond,
        targetLabel: targetLbl,
        affectsAllies: line.affectsAllies ?? false,
        affectsEnemies: line.affectsEnemies ?? false,
        stackRowCaption: stackRowCaption || null,
        stackIndex: line.stackIndex,
        stackMax: line.stackMax,
        // "charge" is the one scaling a card lets the player set (core/spellCharges.js).
        scalingKind: line.scalingKind ?? null,
        randomChance: line.randomChance ?? null,
        randomGroup: line.randomGroup ?? null,
        exclusiveGroupKey: (() => {
            // Element-variant lines must stay as separate per-element rows in the
            // card. exclusiveGroupKey drives the display-collapse, and these
            // lines would otherwise share a key (e.g. the same embedded:* key,
            // which has no element in it). Their mutual exclusivity for damage
            // totals is handled separately via elVariantXorKey.
            if (line.elVariantXorKey) return null;
            const apiKey = line.exclusive_branch_key ?? line.exclusiveBranchKey;
            if (typeof apiKey === 'string' && apiKey.length > 0) return apiKey;
            const effectId = line.effectId ?? null;
            const damageCond = lv?.damage_condition ?? lv?.damageCondition;
            if (damageCond === 'target_under_element_state' && spellId != null) {
                const g = lv?.grade;
                return `target_under_element_xor:${spellId}:${g != null ? g : 'u'}`;
            }
            const isBestElLine = isCasterBestElementLevel(lv) || (line != null && (Number(line.effectId) === 2822 || Number(line.effectId) === 2828));
            if (isBestElLine && spellId != null && effectId != null) {
                const g = lv?.grade;
                return `caster_best_xor:${spellId}:${g != null ? g : 'u'}:${effectId}`;
            }
            const embeddedFrom = line.embeddedFromSpellId ?? null;
            const carrierId = line.embeddingCarrierEffectId ?? null;
            if (embeddedFrom != null || carrierId != null) {
                return `embedded:${embeddedFrom ?? 'x'}:${carrierId ?? 'x'}:${effectId ?? 'x'}`;
            }
            const tm = line.targetMask ?? null;
            const xorBase = maskXorBaseKey(tm);
            if (xorBase != null) {
                return `mask_xor:${xorBase}:${effectId ?? 'x'}:${elKey}`;
            }
            return null;
        })(),
        summonOnlyDmg,
        mpBonusDmg,
        isEmbedded: !!(
            line.embeddingCarrierDescription != null
            || (Array.isArray(line.embeddingConditionStateNames) && line.embeddingConditionStateNames.length > 0)
            || (line.embeddedFromSpellId != null && Number(line.embeddedFromSpellId) > 0)
            || (line.embeddingCarrierEffectId != null && Number(line.embeddingCarrierEffectId) > 0)
        ),
        clientOnly,
        clientOnlyProxy: false,
        triggers: line.triggers ?? 'I',
        delay: line.delay ?? 0,
        sourceKeys: line.sourceKeys || [],
    };
};

const mapBonusLine = (line, sp, stats, lang) => {
    const refId = line.referenceSpellId;
    const selfName = pickI18n(sp?.name, lang) || sp?.name?.en || sp?.name?.fr || String(sp?.id ?? '');
    const spellName = refId != null && Number(refId) === Number(sp?.id)
        ? selfName
        : (refId != null ? `#${refId}` : selfName);
    const baseR = calcRange(line.base, stats, line.el, false);
    const critR = line.crit != null && (hasVal(line.crit.min) || hasVal(line.crit.max))
        ? calcRange(line.crit, stats, line.el, true)
        : null;
    const bonusLabel = fmtBaseDamageBonusLabel({
        spellName,
        min: line.base.min,
        max: line.base.max,
        delay: line.delay,
        duration: line.duration,
        lang,
    });
    return {
        kind: 'base_damage_bonus',
        el: line.el,
        baseRaw: line.base,
        critRaw: line.crit,
        base: baseR,
        crit: critR,
        bonusLabel,
        condition: null,
        targetLabel: null,
    };
};

const ELEMENT_VARIANT_XOR_ELS = new Set(['earth', 'fire', 'water', 'air']);

const rawRangeSig = (r) => {
    if (!r) return 'x:x';
    const mn = hasVal(r?.min) ? toNum(r.min) : 'x';
    const mx = hasVal(r?.max) ? toNum(r.max) : 'x';
    return `${mn}:${mx}`;
};

const elementVariantPairKey = (line) => {
    if (!line || !ELEMENT_VARIANT_XOR_ELS.has(line.el)) return null;
    if (line.kind !== 'damage' && line.kind !== 'heal') return null;
    if (line.isEmbedded || line.clientOnly || line.clientOnlyProxy) return null;
    const trig = line.triggers ?? 'I';
    const delay = Number.isFinite(Number(line.delay)) ? Number(line.delay) : 0;
    const si = Number.isFinite(Number(line.stackIndex)) ? Number(line.stackIndex) : 'x';
    const sm = Number.isFinite(Number(line.stackMax)) ? Number(line.stackMax) : 'x';
    const base = rawRangeSig(line.baseRaw || line.base);
    const crit = rawRangeSig(line.critRaw || line.crit);
    const cond = String(line.condition || '').trim().toLowerCase();
    return `${trig}|${delay}|${si}|${sm}|${base}|${crit}|${cond}`;
};

const annotateElementVariantXorLines = (lines, spellId, scope = 'base') => {
    if (!Array.isArray(lines) || lines.length < 4) return lines || [];
    const dmg = new Map();
    const heal = new Map();
    lines.forEach((line, idx) => {
        const key = elementVariantPairKey(line);
        if (!key) return;
        const bucketMap = line.kind === 'damage' ? dmg : (line.kind === 'heal' ? heal : null);
        if (!bucketMap) return;
        if (!bucketMap.has(key)) bucketMap.set(key, { indices: [], elements: new Set() });
        const bucket = bucketMap.get(key);
        bucket.indices.push(idx);
        bucket.elements.add(line.el);
    });
    const hasFullSet = (bucket) =>
        bucket && bucket.elements.size === 4
        && ['earth', 'fire', 'water', 'air'].every((el) => bucket.elements.has(el));
    const hasAnyHeal = Array.from(heal.values()).some((bucket) => (bucket?.indices?.length || 0) > 0);
    const eligibleKeys = Array.from(dmg.keys()).filter((k) => (
        hasFullSet(dmg.get(k)) && (hasFullSet(heal.get(k)) || hasAnyHeal)
    ));
    if (!eligibleKeys.length) return lines;
    const out = [...lines];
    eligibleKeys.forEach((k, i) => {
        const exKey = `element_variant_xor:${spellId ?? 'x'}:${scope}:${i}`;
        for (const idx of dmg.get(k).indices) {
            out[idx] = {
                ...out[idx],
                exclusiveGroupKey: out[idx].exclusiveGroupKey || exKey,
            };
        }
    });
    return out;
};

/**
 * True when the summon inherits caster stats that actually change spell damage in our model
 * (DofusDB `grades[].bonusCharacteristics` percents). Omits wisdom and lifePoints: they do not
 * feed the spell damage formula here. If only those (or all) are non-zero, hide the summon.
 */
export const summonContextInheritsBuildStats = (rawSc) => {
    if (!rawSc || typeof rawSc !== 'object') return false;
    const bc = rawSc.bonus_characteristics ?? rawSc.bonusCharacteristics;
    if (!bc || typeof bc !== 'object') return false;
    const keys = [
        'strength', 'intelligence', 'chance', 'agility',
        'bonusEarthDamage', 'bonusFireDamage', 'bonusWaterDamage', 'bonusAirDamage',
    ];
    return keys.some((k) => Number(bc[k]) > 0);
};

/**
 * Merge a spell's element_variants into a single spell, concatenating every
 * variant's effect_lines onto level 0. The spell keeps its original numeric id
 * so it resolves correctly through the criterion / scoring / pruning pipeline,
 * which all key rows by Number(spell.id).
 *
 * The spell is cast in exactly one element, so the per-element lines are
 * mutually exclusive for damage totals. We tag them with a dedicated
 * el_variant_xor_group marker (NOT exclusive_branch_key / exclusiveGroupKey):
 * exclusiveGroupKey also drives the card's display-collapse, which would fold
 * all 5 elements into a single visible row. This marker is read only by the
 * total/score code (totalAvgDmg, sumLinesExpectedWithXor), so each element
 * stays a separate, individually selectable line while the headline and
 * generator score treat them as pick-one.
 */
const expandElVariants = (spell) => {
    const lv0 = spell?.levels?.[0];
    const variants = lv0?.element_variants;
    if (!Array.isArray(variants) || variants.length === 0) return [spell];
    const xorGroup = `el_variant_xor:${spell?.id ?? 'x'}`;
    const mergedLines = variants.flatMap((v) => (
        Array.isArray(v?.effect_lines)
            ? v.effect_lines.map((ln) => (
                ln && typeof ln === 'object'
                    ? { ...ln, el_variant_xor_group: xorGroup }
                    : ln
            ))
            : []
    ));
    return [{
        ...spell,
        levels: (spell.levels || []).map((lv, i) =>
            i === 0 ? { ...lv, effect_lines: mergedLines, element_variants: null } : lv
        ),
    }];
};

export const calcSpellRows = ({
    spells,
    lvl,
    stats,
    lang,
    eliotropePortalCardOnly = false,
    summonCatalog = {},
    summonRecursionDepth = 0,
    // `{ spellId: { lineKey: n } }` — charged lines at a chosen count (core/spellCharges.js).
    charges = null,
}) => {
    if (!Array.isArray(spells) || !stats) return [];
    const spells_ = applySpellCharges(spells, charges, rawEffectLineKey).flatMap(expandElVariants);

    const calcSummonStats = (summonerStats, summonerLvl, rawSc) => {
        const bonus = rawSc?.bonus_characteristics || {};
        const newStats = { [POSITION]: summonerStats[POSITION] };

        for (const [k, v] of Object.entries(summonerStats)) {
            newStats[k] = { ...v };
        }

        const getStat = (id) => newStats[id]?.current || 0;
        const setStat = (id, val) => {
            if (!newStats[id]) newStats[id] = { current: 0 };
            newStats[id].current = val;
        };

        // 1. Apply inheritance percentages from bonus_characteristics
        // (e.g. strength: 50 means the summon inherits 50% of the summoner's strength)
        const inheritPct = (statKey) => (Number(bonus[statKey]) || 0) / 100;

        setStat(st.STRENGTH_ID, Math.floor(getStat(st.STRENGTH_ID) * inheritPct('strength')));
        setStat(st.INTELLIGENCE_ID, Math.floor(getStat(st.INTELLIGENCE_ID) * inheritPct('intelligence')));
        setStat(st.CHANCE_ID, Math.floor(getStat(st.CHANCE_ID) * inheritPct('chance')));
        setStat(st.AGILITY_ID, Math.floor(getStat(st.AGILITY_ID) * inheritPct('agility')));
        setStat(st.WISDOM_ID, Math.floor(getStat(st.WISDOM_ID) * inheritPct('wisdom')));

        setStat(ELM_DMG_ID.earth, Math.floor(getStat(ELM_DMG_ID.earth) * inheritPct('bonusEarthDamage')));
        setStat(ELM_DMG_ID.fire, Math.floor(getStat(ELM_DMG_ID.fire) * inheritPct('bonusFireDamage')));
        setStat(ELM_DMG_ID.water, Math.floor(getStat(ELM_DMG_ID.water) * inheritPct('bonusWaterDamage')));
        setStat(ELM_DMG_ID.air, Math.floor(getStat(ELM_DMG_ID.air) * inheritPct('bonusAirDamage')));
        setStat(ELM_DMG_ID.neutral, Math.floor(getStat(ELM_DMG_ID.neutral) * inheritPct('bonusEarthDamage')));

        // For generic damage and power, we use the max inheritance of the 4 elements
        const maxElInherit = Math.max(
            inheritPct('strength'),
            inheritPct('intelligence'),
            inheritPct('chance'),
            inheritPct('agility')
        );
        setStat(st.DAMAGE_ID, Math.floor(getStat(st.DAMAGE_ID) * maxElInherit));
        setStat(st.POWER_ID, Math.floor(getStat(st.POWER_ID) * maxElInherit));

        // 2. Apply base stats
        const base = rawSc?.base_stats || {};
        setStat(st.STRENGTH_ID, getStat(st.STRENGTH_ID) + (base.strength || 0));
        setStat(st.INTELLIGENCE_ID, getStat(st.INTELLIGENCE_ID) + (base.intelligence || 0));
        setStat(st.CHANCE_ID, getStat(st.CHANCE_ID) + (base.chance || 0));
        setStat(st.AGILITY_ID, getStat(st.AGILITY_ID) + (base.agility || 0));
        setStat(st.WISDOM_ID, getStat(st.WISDOM_ID) + (base.wisdom || 0));

        // 3. Apply characRatios
        const ratios = rawSc?.charac_ratios || [];
        ratios.forEach((entry) => {
            if (!Array.isArray(entry) || entry.length < 2) return;
            const [rawStatId, rawRatio] = entry;
            const statId = Number(rawStatId);
            const ratio = Number(rawRatio);
            if (!Number.isFinite(statId) || !Number.isFinite(ratio)) return;
            setStat(statId, getStat(statId) + Math.floor((ratio * summonerLvl) / 10));
        });

        return newStats;
    };

    const annotateClientOnlyProxies = (mappedLines) => {
        if (!Array.isArray(mappedLines) || mappedLines.length < 2) return mappedLines || [];
        const nonClientSigs = new Set(
            mappedLines
                .filter((l) => l?.kind === 'damage' && !l.clientOnly && l.effectId != null)
                .map((l) => `${l.effectId}:${l.el}`),
        );
        if (nonClientSigs.size === 0) return mappedLines;
        return mappedLines.map((l) => {
            const isProxy = l?.kind === 'damage'
                && l.clientOnly
                && l.effectId != null
                && nonClientSigs.has(`${l.effectId}:${l.el}`);
            if (!isProxy) return l;
            const isImmediate = rawEffectTrigger(l) === 'I' && lineDelay(l) === 0;
            return {
                ...l,
                clientOnlyProxy: true,
                stackRowCaption: l.stackRowCaption || (
                    isImmediate ? (CLIENT_ONLY_LBL[lang] || CLIENT_ONLY_LBL.en) : null
                ),
            };
        });
    };
    const rows = spells_.map((sp) => {
        const lv = pickLvl(sp?.levels, lvl);
        const { base, conditional, bonusLines: bonusIn } = getDmgLines(lv);
        const hasStateBranches = conditional.some((g) => g.key === 'target_under_state');
        const dropBaseUnderStateHint = hasStateBranches
            && base.length > 0
            && lv?.damage_condition === 'target_under_state';
        const dmgRowTierMapped = (line) => {
            if (line.summonOnlyDmg) return 2;
            if (line.mpBonusDmg) return 1;
            return 0;
        };
        const sortMappedDmgRows = (a, b) => {
            const ar = dmgRowTierMapped(a);
            const br = dmgRowTierMapped(b);
            if (ar !== br) return ar - br;
            const ai = a.stackIndex;
            const bi = b.stackIndex;
            const au = ai == null || !Number.isFinite(ai);
            const bu = bi == null || !Number.isFinite(bi);
            if (au && bu) return 0;
            if (au) return -1;
            if (bu) return 1;
            return ai - bi;
        };
        const mapOptsBase = {
            dropTargetUnderStateLineHint: dropBaseUnderStateHint,
            spellId: sp?.id ?? null,
        };
        const mappedBase = base.map((line) => mapLine(line, lv, stats, lang, lvl, mapOptsBase)).sort(sortMappedDmgRows);
        const mappedBaseWithClientProxies = annotateClientOnlyProxies(mappedBase);
        const mappedBaseFinal = annotateElementVariantXorLines(mappedBaseWithClientProxies, sp?.id ?? null, 'base');
        const mappedConditionalBlocks = conditional.map((g) => ({
            condKey: g.key,
            label: fmtCondLabel(g.key, g.stateId, lang, g.hasStartOfTurn, g.hasEndOfTurn, g.stateSpecs),
            lines: g.lines.map((line) => {
                const mapped = mapLine(line, lv, stats, lang, lvl, {
                    dropTargetUnderStateLineHint:
                        g.key === 'target_under_state' && lv?.damage_condition === 'target_under_state',
                    spellId: sp?.id ?? null,
                    clientOnlyElementFallback: false,
                });
                if (PORTAL_GATE_KEYS.includes(g.key)) return { ...mapped, portalGate: g.key };
                if (g.key === 'target_under_state' && g.stateSpecs?.length) {
                    return { ...mapped, stateGate: g.stateSpecs };
                }
                return mapped;
            }).sort(sortMappedDmgRows),
        }));
        const mappedConditionalBlocksWithClientProxies = mappedConditionalBlocks.map((block) => ({
            ...block,
            lines: annotateClientOnlyProxies(block.lines || []),
        }));
        const mappedConditionalBlocksFinal = mappedConditionalBlocksWithClientProxies.map((block, bi) => ({
            ...block,
            lines: annotateElementVariantXorLines(
                block.lines || [],
                sp?.id ?? null,
                `cond:${bi}:${block.condKey || ''}`,
            ),
        }));
        const embeddedSig = (line) => {
            const emb = line?.embeddedFromSpellId;
            const car = line?.embeddingCarrierEffectId;
            const eff = line?.effectId;
            const el = line?.el;
            const tm = line?.targetMask ?? '';
            if (emb == null && car == null) return null;
            return `${emb ?? 'x'}:${car ?? 'x'}:${eff ?? 'x'}:${el ?? 'x'}:${tm}`;
        };
        const immediateEmbeddedSigs = new Set(
            mappedBaseFinal
                .filter((line) => line.kind === 'damage' && line.isEmbedded)
                .map((line) => embeddedSig(line))
                .filter(Boolean),
        );
        const promotedEmbeddedTimed = [];
        const conditionalBlocks = mappedConditionalBlocksFinal
            .map((block) => {
                if (block.condKey !== 'start_of_turn' && block.condKey !== 'end_of_turn') return block;
                const kept = [];
                for (const line of block.lines || []) {
                    const sig = embeddedSig(line);
                    const hasOwnCondition = !!line.condition || !!line.targetLabel;
                    const canPromote = line.kind === 'damage'
                        && line.isEmbedded
                        && !hasOwnCondition
                        && !!sig
                        && immediateEmbeddedSigs.has(sig);
                    if (canPromote) {
                        const timedKey = line.exclusiveGroupKey
                            ? `${line.exclusiveGroupKey}:timed:${block.condKey}`
                            : null;
                        promotedEmbeddedTimed.push({
                            ...line,
                            targetLabel: block.label || line.targetLabel || null,
                            exclusiveGroupKey: timedKey,
                        });
                    }
                    else kept.push(line);
                }
                return { ...block, lines: kept };
            })
            .filter((block) => (block.lines || []).length > 0);
        const baseLines = [...mappedBaseFinal, ...promotedEmbeddedTimed].sort(sortMappedDmgRows);
        const bonusLines = (bonusIn || []).map((bl) => mapBonusLine(bl, sp, stats, lang));
        const rawSc = lv?.summon_context ?? lv?.summonContext;
        const summonInheritsBuild = summonContextInheritsBuildStats(rawSc);
        const summonMonsterUrl = summonInheritsBuild && typeof rawSc?.monster_icon_url === 'string'
            ? resolveMediaUrl(rawSc.monster_icon_url)
            : null;
        let summonChildRows = [];
        if (
            summonInheritsBuild
            && summonRecursionDepth < 4
            && rawSc
            && typeof rawSc === 'object'
            && rawSc.monster_id
            && Array.isArray(rawSc.child_spell_ids)
            && rawSc.child_spell_ids.length
        ) {
            summonChildRows = rawSc.child_spell_ids
                .map((cid) => {
                    const key = String(cid);
                    const csp = summonCatalog[key] ?? summonCatalog[cid];
                    if (!csp) return null;
                    const summonStats = calcSummonStats(stats, lvl, rawSc);
                    const nested = calcSpellRows({
                        spells: [csp],
                        lvl,
                        stats: summonStats,
                        lang,
                        eliotropePortalCardOnly,
                        summonCatalog,
                        summonRecursionDepth: summonRecursionDepth + 1,
                    });
                    return nested[0] || null;
                })
                .filter(Boolean);
        }
        const linkedMechanicRows = (
            Array.isArray(sp?.linked_mechanics)
                && sp.linked_mechanics.length > 0
                && summonRecursionDepth < 4
        )
            ? (() => {
                const seen = new Set();
                return sp.linked_mechanics
                    .map((rawId) => Number(rawId))
                    .filter((id) => Number.isFinite(id) && id > 0)
                    .filter((id) => {
                        if (seen.has(id)) return false;
                        seen.add(id);
                        return true;
                    })
                    .map((id) => {
                        const key = String(id);
                        const csp = summonCatalog[key] ?? summonCatalog[id];
                        if (!csp) return null;
                        const nested = calcSpellRows({
                            spells: [csp],
                            lvl,
                            stats,
                            lang,
                            eliotropePortalCardOnly,
                            summonCatalog,
                            summonRecursionDepth: summonRecursionDepth + 1,
                        });
                        return nested[0] || null;
                    })
                    .filter(Boolean);
            })()
            : [];
        summonChildRows = [...summonChildRows, ...linkedMechanicRows];
        const allLines = [...baseLines, ...conditionalBlocks.flatMap((b) => b.lines)];
        const apCost = toNum(lv?.ap_cost) || 1;
        const avgDmg = totalAvgDmg(allLines);
        const dmgPerAp = apCost > 0 ? avgDmg / apCost : avgDmg;
        const canCrit = [...allLines, ...bonusLines].some((line) => hasCritRange(line?.crit));
        const baseCrit = toNum(lv?.base_crit_chance_percent) || 0;
        const buildCrit = getCur(stats, st.CRIT_ID) || 0;
        const critPct = canCrit ? Math.round(baseCrit + buildCrit) : 0;
        const rangeCanBeBoosted = lv?.range_can_be_boosted ?? lv?.rangeCanBeBoosted ?? null;
        const row = {
            id: sp?.id,
            name: sp?.name?.[lang] || sp?.name?.en || sp?.name?.fr || '-',
            description: sp?.description?.[lang] || sp?.description?.en || sp?.description?.fr || '',
            icon: resolveMediaUrl(sp?.icon_url) || null,
            order: toNum(sp?.order),
            damageCondition: lv?.damage_condition ?? lv?.damageCondition ?? null,
            apCost: lv?.ap_cost ?? null,
            critPct,
            canCrit,
            rangeMin: lv?.min_range ?? null,
            rangeMax: applyBuildRangeBonus(lv?.max_range ?? null, rangeCanBeBoosted, stats),
            rangeCanBeBoosted,
            castLos: lv?.cast_los ?? null,
            baseLines,
            conditionalBlocks,
            bonusLines,
            dmgPerAp,
            healLabel: HEAL_LBL[lang] || HEAL_LBL.en,
            cardPrimaryTotal: 0,
            cardPrimaryHeaderTotal: 0,
            summonContext: summonInheritsBuild && rawSc && typeof rawSc === 'object' ? rawSc : null,
            summonMonsterIconUrl: summonMonsterUrl,
            summonChildRows,
        };
        row.cardPrimaryHeaderTotal = calcSpellCardPrimaryHeaderTotal(row, eliotropePortalCardOnly);
        row.cardPrimaryTotal = calcSpellCardPrimaryTotal(row, eliotropePortalCardOnly);
        const hasOwnDmg = row.baseLines.length > 0
            || row.conditionalBlocks.length > 0
            || (row.bonusLines && row.bonusLines.length > 0);
        const summonAgg = (summonChildRows || []).reduce((acc, c) => acc + (Number(c.cardPrimaryTotal) || 0), 0);
        if (summonChildRows.length && !hasOwnDmg && summonAgg > 0) {
            row.cardPrimaryTotal = summonAgg;
            row.cardPrimaryHeaderTotal = summonAgg;
        }

        const nStr = row.name || '';
        const isGrayedOut = nStr === 'Bluff' || nStr === 'Tromperie' || nStr === 'Rekop';
        if (isGrayedOut) {
            row.isDisabled = true;
            row.baseLines = [];
            row.conditionalBlocks = [];
            row.bonusLines = [];
            row.summonChildRows = [];
            row.cardPrimaryTotal = 0;
            row.cardPrimaryHeaderTotal = 0;
        }

        return row;
    }).filter((row) =>
        row.isDisabled
        || row.baseLines.length > 0
        || row.conditionalBlocks.length > 0
        || (row.bonusLines && row.bonusLines.length > 0)
        || (row.summonChildRows && row.summonChildRows.length > 0));

    const primaryPerAp = (row) => {
        const ap = toNum(row.apCost) || 1;
        return (row.cardPrimaryTotal || 0) / ap;
    };

    const collectDmgEls = (row) => {
        const out = new Set();
        let hasPush = false;
        const all = [...(row.baseLines || []), ...(row.conditionalBlocks || []).flatMap((b) => b.lines || [])];
        for (const line of all) {
            if (!line || line.kind !== 'damage' || line.clientOnlyProxy) continue;
            if (line.el === 'push') {
                hasPush = true;
                continue;
            }
            if (ELS.includes(line.el)) out.add(line.el);
        }
        return { els: out, hasPush };
    };

    const byEl = {};
    for (const row of rows) {
        if (row?.damageCondition === 'caster_best_element') continue;
        const info = collectDmgEls(row);
        if (info.els.size !== 1) continue;
        const el = [...info.els][0];
        if (!byEl[el]) byEl[el] = { sum: 0, n: 0 };
        byEl[el].sum += Number(row.dmgPerAp) || 0;
        byEl[el].n += 1;
    }
    const elRank = {};
    Object.entries(byEl)
        .map(([el, v]) => ({ el, avg: v.n > 0 ? v.sum / v.n : 0 }))
        .sort((a, b) => b.avg - a.avg)
        .forEach((x, i) => { elRank[x.el] = i; });

    const bucketKey = (row) => {
        if (row?.damageCondition === 'caster_best_element') {
            return { rank: 0, sub: 0 }; // 1) caster's best-element spells first
        }
        const info = collectDmgEls(row);
        if (info.els.size === 0) {
            if (info.hasPush) return { rank: 2000, sub: 0 }; // 5) push-only last
            return { rank: 3000, sub: 0 };
        }
        if (info.els.size === 1) {
            const el = [...info.els][0];
            const rk = elRank[el];
            return { rank: 2 + (rk != null ? rk : 999), sub: 0 }; // 3) mono-element families by avg rank
        }
        return { rank: 1, sub: 0 }; // 2) multi-element spells
    };

    const rowsWithCategory = rows.map((row) => ({
        ...row,
        sortCategoryRank: bucketKey(row).rank,
    }));

    return rowsWithCategory.sort((a, b) => {
        if (a.sortCategoryRank !== b.sortCategoryRank) return a.sortCategoryRank - b.sortCategoryRank;
        const d2 = (b.cardPrimaryTotal || 0) - (a.cardPrimaryTotal || 0);
        if (d2 !== 0) return d2;
        const d = primaryPerAp(b) - primaryPerAp(a);
        if (d !== 0) return Math.sign(d);
        return (b.dmgPerAp || 0) - (a.dmgPerAp || 0);
    });
};

const WEAPON_ELM_BY_ID = {
    0: 'neutral',
    1: 'earth',
    2: 'fire',
    3: 'water',
    4: 'air',
    5: 'push',
};
const BEST_ELEMENT_WEAPON_EFFECT_IDS = new Set([2822, 2828]);
const BEST_ELEMENT_WEAPON_STEAL_EFFECT_IDS = new Set([2828]);
// Every steal a weapon can carry, best-element included. Only 2828 used to
// count, so an ordinary "vol de vie Feu" line was drawn and captioned as plain
// damage — the heart is `weapon.js`'s list, and this is the same list.
const isWeaponStealEffectId = (id) =>
    LIFESTEAL_EFFECT_IDS.has(id) || BEST_ELEMENT_WEAPON_STEAL_EFFECT_IDS.has(id);
const PUSH_WEAPON_EFFECT_IDS = new Set([5]);

const clampPct = (v) => Math.max(0, Math.min(100, toNum(v)));

const hasCritRange = (range) =>
    !!range && (hasVal(range.min) || hasVal(range.max));

const canWeaponCrit = (weapon) => {
    if (!weapon || typeof weapon !== 'object') return false;
    if (typeof weapon.can_crit === 'boolean') return weapon.can_crit;
    return toNum(weapon.crit_chance) > 0 || toNum(weapon.crit_bonus) > 0;
};

const isBestElementWeaponEffectId = (id) =>
    id != null && BEST_ELEMENT_WEAPON_EFFECT_IDS.has(toNum(id));

const calcBestWeaponElementVal = ({ base, stats, isCrit }) => Math.max(
    ...BEST_ELEMENT_KEYS.map((el) => calcWeaponVal({ base, stats, el, isCrit })),
);

const calcBestWeaponElementRange = (range, stats, isCrit) => {
    if (!range || (!hasVal(range.min) && !hasVal(range.max))) return { min: null, max: null };
    return {
        min: hasVal(range.min) ? calcBestWeaponElementVal({ base: range.min, stats, isCrit }) : null,
        max: hasVal(range.max) ? calcBestWeaponElementVal({ base: range.max, stats, isCrit }) : null,
    };
};

const resolveBestWeaponElementForRawRange = (range, stats, isCrit) => {
    if (!range || (!hasVal(range.min) && !hasVal(range.max))) return BEST_ELEMENT_KEYS[0];
    let bestEl = BEST_ELEMENT_KEYS[0];
    let bestSum = -Infinity;
    for (const el of BEST_ELEMENT_KEYS) {
        let sum = 0;
        let n = 0;
        if (hasVal(range.min)) {
            sum += calcWeaponVal({ base: range.min, stats, el, isCrit });
            n++;
        }
        if (hasVal(range.max)) {
            sum += calcWeaponVal({ base: range.max, stats, el, isCrit });
            n++;
        }
        if (n === 0) continue;
        if (sum > bestSum) {
            bestSum = sum;
            bestEl = el;
        }
    }
    return bestEl;
};

const weaponEffectDiceRange = (ef) => {
    const min = Number.isFinite(Number(ef?.min)) ? Number(ef.min) : null;
    const rawMax = Number.isFinite(Number(ef?.max)) ? Number(ef.max) : null;
    const max = rawMax != null && rawMax > 0 ? rawMax : min;
    return { min, max };
};

/**
 * One weapon effect resolved against a stat sheet: the element it lands as, the
 * dice it rolls, what those dice become, and its expected value once crits are
 * blended in.
 *
 * `healMath` is the one difference between the two callers. A heal line is
 * damage arithmetic to `calcWeaponCardRow`, because that row feeds the generator
 * score and the backend oracle it must agree with is the authority on what the
 * number means. Everywhere the number is only shown to a person, a heal is
 * computed as a heal.
 */
const resolveWeaponEffect = (ef, { stats, lvl, canCrit, critBonus, critRate, healMath = false }) => {
    const effectId = toNum(ef.effect_id);
    const bestElement = isBestElementWeaponEffectId(effectId);
    const isPushEffect = effectId != null && PUSH_WEAPON_EFFECT_IDS.has(effectId);
    const isHeal = HEAL_EFFECT_IDS.has(effectId);
    const { min, max } = weaponEffectDiceRange(ef);
    const rawRange = { min, max };
    const el = bestElement
        ? resolveBestWeaponElementForRawRange(rawRange, stats, false)
        : (isPushEffect ? 'push' : WEAPON_ELM_BY_ID[Number(ef.element_id)]);
    if (!el) return null;
    if (min == null && max == null) return null;

    if (el === 'push') {
        const range = calcPushDamage(rawRange, stats, lvl);
        const pushVal = hasVal(range?.max) ? range.max : range?.min;
        if (!hasVal(pushVal)) return null;
        const dice = { min, max: max ?? min };
        return {
            effectId,
            kind: 'push',
            el,
            isLifesteal: false,
            raw: dice,
            // Pushback lands the same on a crit — `calcPushDamage` has no crit
            // term — so the crit column repeats it rather than reading as a line
            // that does nothing on a crit. It is already in the crit total.
            critRaw: canCrit ? dice : null,
            range,
            critRange: canCrit ? range : null,
            avg: Math.round(pushVal),
        };
    }

    const baseMaxSrc = max ?? min;
    const critMinSrc = (min != null ? min : (max ?? 0)) + critBonus;
    const critMaxSrc = (max ?? min ?? 0) + critBonus;
    const useHealMath = isHeal && healMath;
    const weaponPower = getCur(stats, st.WEAPON_POWER_ID);

    const value = (base, isCrit) => {
        if (useHealMath) return calcHealVal({ base, stats, el, effectId, weaponPower });
        return bestElement
            ? calcBestWeaponElementVal({ base, stats, isCrit })
            : calcWeaponVal({ base, stats, el, isCrit });
    };
    const rangeOf = (from, isCrit) => {
        if (useHealMath) return calcHealRange(from, stats, el, effectId, weaponPower);
        if (bestElement) return calcBestWeaponElementRange(from, stats, isCrit);
        return {
            min: from.min != null ? value(from.min, isCrit) : null,
            max: from.max != null ? value(from.max, isCrit) : null,
        };
    };

    const avgBase = ((min ?? 0) + (max ?? (min ?? 0))) / 2;
    const normal = value(avgBase, false);
    const avg = canCrit
        ? Math.round((1 - critRate) * normal + critRate * value(avgBase + critBonus, true))
        : Math.round(normal);

    const range = rangeOf({ min, max: baseMaxSrc }, false);
    if (range.max == null && range.min != null) range.max = range.min;

    return {
        effectId,
        kind: isHeal ? 'heal' : 'damage',
        el,
        isLifesteal: isWeaponStealEffectId(effectId),
        raw: { min, max: max ?? min },
        critRaw: canCrit
            ? {
                min: (min != null ? min : (max ?? null)) != null ? critMinSrc : null,
                max: (max ?? min ?? null) != null ? critMaxSrc : null,
            }
            : null,
        range,
        critRange: canCrit ? rangeOf({ min: critMinSrc, max: critMaxSrc }, true) : null,
        avg,
    };
};

/** Crit rate a weapon actually lands at for this sheet, and whether it can crit at all. */
const weaponCritContext = (rawWeapon, weapon, stats) => {
    const canCrit = canWeaponCrit(rawWeapon);
    const critPct = canCrit
        ? clampPct((toNum(weapon.crit_chance) || 0) + (getCur(stats, st.CRIT_ID) || 0))
        : 0;
    return { canCrit, critPct, critRate: critPct / 100, critBonus: canCrit ? (toNum(weapon.crit_bonus) || 0) : 0 };
};

const weaponEffectsOf = (weapon) =>
    weapon.effects.filter((ef) => ef && Number.isFinite(Number(ef.element_id)));

export const calcWeaponCardRow = ({
    weaponItem,
    stats,
    lvl = null,
    lang = 'fr',
    weaponMaging = null,
}) => {
    const rawWeapon = weaponItem?.weapon;
    if (!rawWeapon || !Array.isArray(rawWeapon.effects) || !stats) return null;
    const weapon = weaponMaging
        ? applyWeaponMagingPreview(rawWeapon, weaponMaging)
        : rawWeapon;

    const { canCrit, critPct, critRate, critBonus } = weaponCritContext(rawWeapon, weapon, stats);
    const ctx = { stats, lvl, canCrit, critBonus, critRate, healMath: true };

    let total = 0;
    const baseLines = [];

    weaponEffectsOf(weapon).forEach((ef) => {
        const line = resolveWeaponEffect(ef, ctx);
        if (!line) return;
        // A weapon heals whatever it hits, so its heal counts against it —
        // `spell_scoring._line_from_weapon_effect` marks the line `subtract` and
        // the solvers negate it there. Computed as a heal, not as damage: the
        // health handed over scales on Soin and the element's primary, which is
        // what `healMath` above asks resolveWeaponEffect for.
        total += line.kind === 'heal' ? -line.avg : line.avg;
        baseLines.push({
            // The kind the effect actually is, so the detail table draws a heal
            // as a heal: its own icon, its own colour, and "Soins" for a label
            // rather than the fallback "Direct". Every line was flattened to
            // 'damage' back when the card's total added heals in; the total
            // above subtracts them now, and the rows it lists should say so.
            kind: line.kind,
            el: line.el,
            effectId: line.effectId,
            isLifesteal: line.isLifesteal,
            // Raw dice shown as subtle "base" row in SpellDetails.
            baseRaw: line.raw,
            // Weapon crit base is inferred from base + weapon crit bonus.
            critRaw: line.critRaw,
            base: line.range,
            crit: line.critRange,
            condition: line.isLifesteal
                ? COND_REGISTRY.health_steal[(TXT_EN[lang] || TXT_EN.en)]
                : null,
            targetLabel: null,
        });
    });

    if (total <= 0 && baseLines.length === 0) return null;

    const name = weaponItem?.name?.[lang] || weaponItem?.name?.fr || weaponItem?.name?.en || weaponItem?.name || '-';
    const apCost = toNum(weapon.ap) || null;
    const description = buildWeaponCardDescription(weaponItem, weapon, lang) || '';
    return {
        id: 'weapon',
        name,
        description,
        icon: resolveMediaUrl(weaponItem?.icon) || null,
        cardPrimaryTotal: total,
        apCost,
        // Casts per turn the game allows; the find-the-best-weapon row reads it
        // to price a whole turn. Missing means one, like most weapons.
        maxCast: Math.max(1, toNum(weapon.max_cast) || 1),
        critPct,
        canCrit,
        rangeMin: Number.isFinite(Number(weapon.min_range)) ? Number(weapon.min_range) : null,
        rangeMax: Number.isFinite(Number(weapon.max_range)) ? Number(weapon.max_range) : null,
        castLos: typeof weapon.cast_los === 'boolean' ? weapon.cast_los : null,
        baseLines,
        conditionalBlocks: [],
        bonusLines: [],
        healLabel: HEAL_LBL[lang] || HEAL_LBL.en,
    };
};

/**
 * What a weapon does on an item card: one entry per effect, with the dice it
 * rolls and what those dice come to on this build's sheet, plus the expected
 * damage of a single hit.
 *
 * Heals are computed as heals here and left out of `damageTotal` — a shovel that
 * heals 1-80 Fire is not an 80-damage shovel. That is where this parts company
 * with `calcWeaponCardRow`, which counts them because the generator score does.
 * They come back in `healRange`/`critHealRange` instead of netting against the
 * damage, because netting is unreadable on a weapon whose heal dice are wider
 * than its damage dice: Baguette Rhon's hit spans -1351 to +230, so a single
 * signed range says neither what it heals nor what it hits. The card puts the
 * two totals on their own rows and lets the reader subtract.
 *
 * Returns null without a stat sheet: there is nothing to compute, and the dice
 * are already in the effect the caller is holding.
 */
export const calcWeaponHitLines = ({ weapon, stats, lvl = null }) => {
    if (!weapon || !Array.isArray(weapon.effects)) return null;
    const { canCrit, critPct, critRate, critBonus } = weaponCritContext(weapon, weapon, stats);
    const ctx = { stats, lvl, canCrit, critBonus, critRate, healMath: true };

    const lines = [];
    let damageTotal = 0;
    let healTotal = 0;
    // The totals are shown as ranges like every other line, so they sum the lines'
    // ranges; `damageTotal` stays the expected value, which is what ranks weapons.
    const damageRange = { min: 0, max: 0 };
    const critDamageRange = { min: 0, max: 0 };
    const healRange = { min: 0, max: 0 };
    const critHealRange = { min: 0, max: 0 };
    const accumulate = (range, critRange, line) => {
        range.min += toNum(line.range?.min ?? line.range?.max);
        range.max += toNum(line.range?.max ?? line.range?.min);
        // A line that cannot crit still lands on a crit, for its normal value.
        const crit = line.critRange || line.range;
        critRange.min += toNum(crit?.min ?? crit?.max);
        critRange.max += toNum(crit?.max ?? crit?.min);
    };
    // `index` is the effect's position in `weapon.effects`, so a caller rendering
    // the raw effect list can line each row up with its computed values.
    weapon.effects.forEach((ef, index) => {
        if (!ef || !Number.isFinite(Number(ef.element_id))) return;
        const line = stats ? resolveWeaponEffect(ef, ctx) : null;
        if (!line) return;
        lines.push({ ...line, index });
        if (line.kind === 'heal') {
            healTotal += line.avg;
            accumulate(healRange, critHealRange, line);
            return;
        }
        damageTotal += line.avg;
        accumulate(damageRange, critDamageRange, line);
    });

    if (!lines.length) return null;
    return {
        lines, canCrit, critPct, damageTotal, healTotal,
        damageRange, critDamageRange, healRange, critHealRange,
    };
};
